// Chatbularis IA — página del asistente jurídico.
//
// Portada del paquete de svabogadas. La interfaz y los estilos son los mismos
// (las clases al-* del CSS son las mismas); lo que cambió es de dónde
// salen los datos:
//
//   Firebase Auth + rol en Firestore → perfilActual() del panel
//   httpsCallable(consultarAsistente) → POST /asistente/consulta
//   Firestore chatHistorial/onSnapshot → GET /asistente/conversaciones
//   "Indexar Drive"                    → base jurídica en S3 + pgvector
//
// El historial ya no es global del estudio: cada abogado ve el suyo, y el
// aislamiento entre estudios lo garantiza el RLS del servidor.
import React, { useState, useRef, useEffect, useCallback } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { LogoChatbularisSVG } from '../../components/Icons';
import Bloqueo, { type DatosPago } from '../../components/Bloqueo';
import { usePagoVuelta, ResumenPago, HistorialPagos, pagarSaldoIa } from '../../components/PagosEnLinea';
import { perfilActual, signOut, auth, marcarConPanel, signInWithEmailAndPassword, solicitarReset, type Perfil } from '../../utils/auth';
import { TENANT } from '../../utils/api';
import * as IA from '../../utils/asistente';
import * as App from '../../utils/instalarApp';
import { descargarEscrito, familiaPdf, type FormatoEscrito, type OpcionesEscrito } from '../../utils/exportarEscrito';
import './AsistenteLegal.css';

const EJEMPLOS = [
  `TUTELA LABORAL — Vulneración de garantía de indemnidad. Trabajadora María González, RUT 12.345.678-9, se desempeñaba como vendedora en Retail S.A. desde marzo 2021, sueldo base $520.000 + comisiones promedio $380.000. En octubre 2024 interpuso denuncia ante la Inspección del Trabajo por incumplimiento de jornada. Dos semanas después fue despedida bajo causal necesidades de la empresa. Solicitar tutela con nulidad del despido, reincorporación o indemnización sustitutiva, más recargo del 80%, daño moral $5.000.000.`,
  `NULIDAD DEL DESPIDO por no pago de cotizaciones previsionales. Trabajador Pedro Muñoz, RUT 9.876.543-2, operario en Constructora del Sur Ltda., 8 años de servicio continuos, último sueldo $850.000. Despedido el 15 de enero de 2025 por causal art. 161 CT. Empleador mantiene deuda previsional en AFP Habitat y Fonasa por los últimos 14 meses. Solicitar nulidad del despido, pago de remuneraciones durante periodo de separación, indemnización por años de servicio con recargo 50%, feriado proporcional y horas extras no pagadas estimadas en $2.400.000.`,
  `ALIMENTOS DEFINITIVOS — Madre: Claudia Pérez, RUT 15.234.567-8, trabaja como auxiliar de enfermería, sueldo $480.000, arrienda en $220.000, tiene a cargo dos hijos: Sofía (7 años) y Martín (4 años). Padre: Roberto Silva, RUT 11.222.333-4, trabaja como técnico en empresa minera, sueldo acreditado $2.100.000, vive con conviviente sin hijos. No paga alimentos desde agosto 2024. Solicitar alimentos definitivos equivalentes al 40% del ingreso del padre, más pensión de alimentos provisorios urgentes mientras dura el juicio.`,
  `DEMANDA LABORAL — Cobro de prestaciones. Trabajadora Carla Rojas, RUT 16.789.012-3, ejerció como asistente administrativa en Clínica Dental SpA por 3 años y 4 meses, sueldo $680.000. Empresa le exigía firmar liquidaciones con montos distintos a los reales y trabajar sábados sin pago de horas extras (aproximadamente 6 horas semanales). Fue despedida verbalmente el 2 de diciembre de 2024 sin carta de aviso ni pago de indemnizaciones. Solicitar: indemnización sustitutiva del aviso, indemnización por años de servicio, feriado proporcional, horas extraordinarias de los últimos 2 años, diferencias de remuneraciones y nulidad del despido.`,
];

/** Quién entra. Son los mismos roles que exige el servidor; si acá se
 *  ampliara sin ampliarlo allá, el usuario vería la página y recibiría un 403
 *  al escribir. */
const ROLES_CON_ACCESO = ['admin', 'abogada', 'abogado_asociado', 'procuradora'];

const fmtFecha = (iso: string) => {
  const d = new Date(iso);
  return isNaN(d.getTime()) ? '' : d.toLocaleDateString('es-CL', { day: '2-digit', month: 'short' });
};

/** Pesos, sin decimales. El saldo se lleva en pesos enteros. */
const clp = (n: number) => '$' + Math.round(n).toLocaleString('es-CL');

/** "septiembre", en hora de Chile: el mes del contador es el que el servidor
 *  usa para cortar (ver inicioDelMes en el backend), no el del reloj del
 *  navegador. */
const nombreDelMes = () => new Date().toLocaleDateString('es-CL', { month: 'long', timeZone: 'America/Santiago' });

/** Cómo se nombra un razonamiento en la marca de una respuesta ya escrita. */
const textoRazonamiento = (r: IA.Razonamiento) => {
  const def = IA.RAZONAMIENTOS.find(x => x.id === r);
  return def ? `Modo ${def.etiqueta.toLowerCase()} · ${def.modelo}` : '';
};

/** Pantalla angosta (celular): el historial va encima del chat y los
 *  documentos parten plegados. */
const esAngosto = () => typeof window !== 'undefined' && window.matchMedia('(max-width: 800px)').matches;
const CLAVE_DOCS_ABIERTOS = 'al-docs-abiertos';

/** La conversación en la que se estaba trabajando, para volver a ella al
 *  entrar. En el teléfono, Safari recarga la página si se pasa un rato en
 *  otra aplicación, y el abogado volvía a una consulta en blanco sin saber
 *  dónde quedó la suya. Solo si es reciente: al día siguiente se empieza de
 *  cero, como siempre. */
const CLAVE_CONV_ABIERTA = 'al-conv-abierta';
const CONV_RECIENTE_MS = 60 * 60 * 1000;
const recordarConversacion = (id: string | null) => {
  try {
    if (id) localStorage.setItem(CLAVE_CONV_ABIERTA, JSON.stringify({ id, ts: Date.now() }));
    else localStorage.removeItem(CLAVE_CONV_ABIERTA);
  } catch { /* sin almacenamiento */ }
};
const conversacionReciente = (): string | null => {
  try {
    const v = JSON.parse(localStorage.getItem(CLAVE_CONV_ABIERTA) || 'null');
    return v && typeof v.id === 'string' && Date.now() - v.ts < CONV_RECIENTE_MS ? v.id : null;
  } catch {
    return null;
  }
};

/** Que no se vuelva a ofrecer instalar la app a quien ya dijo que no. */
const CLAVE_APP_DESCARTADA = 'al-app-descartada';

/** Espera `ms`, o menos si la persona vuelve a la pestaña: quien desbloquea el
 *  teléfono quiere ver su respuesta ya, no en la próxima vuelta del reloj. */
const pausaOVuelta = (ms: number) => new Promise<void>((listo) => {
  const fin = () => { clearTimeout(t); document.removeEventListener('visibilitychange', alVolver); listo(); };
  const alVolver = () => { if (document.visibilityState === 'visible') fin(); };
  const t = setTimeout(fin, ms);
  document.addEventListener('visibilitychange', alVolver);
});

/** Pide que la pantalla no se apague mientras Chatbularis responde. En el
 *  teléfono, que la pantalla se bloquee a mitad de un escrito es lo que corta
 *  la conexión. Donde el navegador no lo permite, no pasa nada. Devuelve cómo
 *  soltarlo. */
const mantenerPantallaEncendida = async (): Promise<() => void> => {
  try {
    const bloqueo = await (navigator as any).wakeLock?.request('screen');
    return () => { bloqueo?.release?.().catch(() => {}); };
  } catch {
    return () => {};
  }
};

const AsistenteLegal: React.FC = () => {
  const perfil = perfilActual();
  const tieneAcceso = !!perfil && ROLES_CON_ACCESO.includes(perfil.rol);
  // Estudio que solo contrató Chatbularis: no tiene panel de causas. Acá se
  // le propone, y los enlaces "al panel" no pueden llevarlo a una pantalla
  // que lo rebota de vuelta.
  const sinPanel = perfil?.conPanel === false;
  const [verPanel, setVerPanel] = useState(false);
  const [verEquipo, setVerEquipo] = useState(false);

  const [conversaciones, setConversaciones] = useState<IA.ConversacionResumen[]>([]);
  const [convActualId, setConvActualIdEstado] = useState<string | null>(null);
  /** La conversación abierta, también en una referencia: las funciones async
   *  que encadenan pasos (vincular un caso, leer sus documentos, mandar la
   *  pregunta) corren dentro de un mismo cierre y verían el estado viejo.
   *  Pasó: confirmar un caso abría una conversación para los documentos y
   *  otra para la respuesta. Se cambia siempre por `fijarConv`. */
  const convActualRef = useRef<string | null>(null);
  const setConvActualId = (id: string | null) => { convActualRef.current = id; setConvActualIdEstado(id); recordarConversacion(id); };
  const [mensajes, setMensajes] = useState<IA.MensajeIA[]>([]);
  const [documentos, setDocumentos] = useState<IA.DocumentoAdjunto[]>([]);
  /** Los adjuntos tal como están AHORA, para leerlos desde funciones async
   *  que se crearon con un estado más viejo (la lectura de documentos del
   *  caso corre después de vincular, y vincular ya pudo cambiar la lista). */
  const documentosRef = useRef(documentos);
  useEffect(() => { documentosRef.current = documentos; }, [documentos]);
  const [cargandoHistorial, setCargandoHistorial] = useState(true);
  const [input, setInput] = useState('');
  const [cargando, setCargando] = useState(false);
  /** Progreso de la subida: {hecho} de {total}. Con varios archivos, y sobre
   *  todo con imágenes —que hay que transcribir—, un reloj de arena sin número
   *  parece que se colgó. */
  const [subiendo, setSubiendo] = useState<{ hecho: number; total: number } | null>(null);
  const [error, setError] = useState('');
  // Aviso de cuota. Se muestra cuando queda poco, para que el estudio se entere
  // ANTES de quedarse sin redactor a mitad de un escrito y no cuando ya no
  // puede seguir.
  const [avisoSaldo, setAvisoSaldo] = useState('');
  /** Con cuál de los dos modelos se responde. Arranca en lo último que eligió
   *  esta persona en este estudio: quien pasa la mañana revisando demandas no
   *  tiene por qué volver a elegir en cada consulta. */
  const [razonamiento, setRazonamiento] = useState<IA.Razonamiento>(IA.razonamientoGuardado);
  /** Cuota del estudio. Se muestra al pie de la barra lateral para que el
   *  estudio vea cuánto le queda sin tener que preguntarlo: enterarse de que se
   *  acabó cuando el servidor rechaza la consulta, a mitad de una demanda, es
   *  tarde. Si no carga, no se muestra nada — es un dato de apoyo y no puede
   *  estorbar el trabajo. */
  const [saldo, setSaldo] = useState<IA.EstadoSaldo | null>(null);
  const [verSaldo, setVerSaldo] = useState(() => new URLSearchParams(window.location.search).has('cargar'));
  /** Vuelta de Banchile (?pago=<id>): el resumen del pago, que se pregunta
   *  hasta que Banchile lo confirme. Se declara más abajo, cuando ya existe
   *  `recargarSaldo`. */
  /** Bloqueo comercial del estudio (período vencido, suspendido). El cliente
   *  API lo avisa por evento cuando cualquier petición vuelve con motivo, y
   *  acá se cambia la pantalla entera: seguir mostrando el chat con cada
   *  consulta fallando es dejar a la persona sin saber qué pasa ni cómo se
   *  arregla. En modo lectura (gracia) el chat se ve, pero no se puede
   *  consultar: se abre el diálogo de plan con la forma de pagar. */
  const [bloqueo, setBloqueo] = useState<null | { motivo: string; error: string; pago?: DatosPago; lecturaHasta?: string }>(null);
  useEffect(() => {
    const onBloqueo = (ev: Event) => {
      const d = (ev as CustomEvent).detail || {};
      if (d.motivo === 'sin_panel') return; // acá sí puede estar
      if (d.motivo === 'solo_lectura') { setError(d.error || 'Tu período terminó.'); return; }
      setBloqueo(d);
    };
    window.addEventListener('tabularis:bloqueo', onBloqueo);
    return () => window.removeEventListener('tabularis:bloqueo', onBloqueo);
  }, []);
  // Sin saldo: el diálogo para cargar se abre solo, una vez, al entrar. Quien
  // llega y no puede preguntar tiene que ver de inmediato qué hacer, no
  // descubrirlo al apretar Enviar.
  const abiertoSolo = useRef(false);
  // Volviendo de pagar no se abre el diálogo de saldo encima del resumen.
  const [hayPagoEnLaDireccion] = useState(() => new URLSearchParams(window.location.search).has('pago'));
  useEffect(() => {
    if (saldo && saldo.agotado && !saldo.sinTope && !abiertoSolo.current && !hayPagoEnLaDireccion) { abiertoSolo.current = true; setVerSaldo(true); }
  }, [saldo, hayPagoEnLaDireccion]);
  /** Lo que el modelo lleva escrito de la respuesta en curso, y cuánto lleva
   *  pensando antes de la primera palabra. Separar las dos cosas importa: son
   *  dos esperas distintas y solo una tiene algo que mostrar. */
  const [enVivo, setEnVivo] = useState<{ texto: string; segundos: number } | null>(null);
  /** El navegador perdió la conexión, pero la consulta sigue en el servidor y
   *  la pantalla está esperando que se guarde. */
  const [esperando, setEsperando] = useState(false);
  /** El último error que fue de una consulta fallida. Sirve para no volver a
   *  mostrarlo como "aviso previo" después de una respuesta que sí llegó. */
  const errorDeConsulta = useRef('');
  /** El caso del panel sobre el que trabaja la conversación abierta. Lo pone
   *  el servidor (viene con la conversación y con cada respuesta); acá solo se
   *  muestra y se cambia a pedido del abogado. */
  const [caso, setCaso] = useState<IA.CasoVinculado | null>(null);
  /** El caso vinculado ya no se puede ver: la última respuesta salió sin la
   *  ficha, y hay que decirlo. */
  const [casoPerdido, setCasoPerdido] = useState(false);
  /** El servidor pidió confirmar de qué caso habla la pregunta antes de
   *  responderla. Se guarda la pregunta para reenviarla con la respuesta. */
  const [confirmar, setConfirmar] = useState<(IA.ConfirmacionCaso & { pregunta: string; desdePanel?: boolean }) | null>(null);
  const [verCaso, setVerCaso] = useState(false);
  const [verBiblioteca, setVerBiblioteca] = useState(false);
  /** El formato de los archivos del estudio (letra, tamaño, interlineado,
   *  márgenes). Se carga al entrar y se vuelve a pedir al cerrar la
   *  biblioteca, que es donde se cambia. */
  const [formato, setFormato] = useState<IA.FormatoEstudio | null>(null);
  const recargarFormato = useCallback(async () => {
    try { setFormato((await IA.listarBiblioteca()).formato); } catch { /* sin formato se usa el de siempre */ }
  }, []);
  // En celular el historial se abre encima del chat y parte cerrado: abierto
  // de entrada tapaba la conversación y había que cerrarlo para escribir.
  const [sidebarOpen, setSidebarOpen] = useState(() => !esAngosto());
  const cerrarSiAngosto = () => { if (esAngosto()) setSidebarOpen(false); };
  // Los documentos de la conversación se pueden plegar: con los de un caso
  // completo ocupaban media pantalla. Lo elegido se recuerda en este
  // navegador; sin preferencia, abiertos en computador y plegados en celular.
  const [docsAbiertos, setDocsAbiertos] = useState<boolean>(() => {
    try { const v = localStorage.getItem(CLAVE_DOCS_ABIERTOS); if (v === '1' || v === '0') return v === '1'; } catch { /* sin almacenamiento */ }
    return !esAngosto();
  });
  const alternarDocs = () => setDocsAbiertos(v => {
    try { localStorage.setItem(CLAVE_DOCS_ABIERTOS, v ? '0' : '1'); } catch { /* sin almacenamiento */ }
    return !v;
  });
  // El cuadro crece con lo que se escribe, hasta un tope: fijo en tres líneas
  // se escribía a ciegas un caso largo, y fijo alto se comía la pantalla.
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  useEffect(() => {
    const el = textareaRef.current;
    if (!el) return;
    el.style.height = 'auto';
    el.style.height = `${Math.min(el.scrollHeight, Math.round(window.innerHeight * 0.35))}px`;
  }, [input]);
  const [mostrarEjemplos, setMostrarEjemplos] = useState(false);
  const bottomRef = useRef<HTMLDivElement>(null);
  const fileRefChat = useRef<HTMLInputElement>(null);

  const vacio = mensajes.length === 0;

  const recargarSaldo = useCallback(async () => {
    try {
      setSaldo(await IA.estadoSaldo());
    } catch {
      // Silencio a propósito: un contador que no cargó no es un problema que
      // el abogado pueda resolver ni que le impida escribir.
    }
  }, []);
  // Vuelta de Banchile: el resumen del pago (ver components/PagosEnLinea).
  // Al aprobarse una carga, el pie de saldo se pone al día.
  const [pagoVuelta, cerrarPagoVuelta] = usePagoVuelta(tieneAcceso, () => void recargarSaldo());


  const recargarHistorial = useCallback(async () => {
    try {
      setConversaciones(await IA.listarConversaciones());
    } catch {
      // Un historial que no carga no debe impedir escribir una consulta nueva.
    } finally {
      setCargandoHistorial(false);
    }
  }, []);

  useEffect(() => {
    if (!tieneAcceso) return;
    void recargarHistorial();
    void recargarSaldo();
    void recargarFormato();
  }, [tieneAcceso, recargarHistorial, recargarSaldo, recargarFormato]);

  // Llegó desde la ficha de un caso del panel ("Redactar con Chatbularis").
  // Aun así se confirma: el diálogo muestra cliente, RUT, código y rol antes
  // de vincular nada. Es un clic más y es el clic que evita trabajar sobre el
  // caso equivocado con la pestaña de al lado abierta en otro.
  useEffect(() => {
    if (!tieneAcceso || sinPanel) return;
    const params = new URLSearchParams(window.location.search);
    const id = params.get('caso');
    if (!id) return;
    // Qué se va a redactar, si lo eligió en la ficha ("Demanda de alimentos").
    const tarea = (params.get('redactar') || '').trim().slice(0, 120);
    IA.fichaCaso(id)
      // La dirección se limpia recién cuando la ficha llegó: si falla (la
      // sesión caducó, por ejemplo), el ?caso= sigue ahí para reintentar.
      .then((f) => { window.history.replaceState(null, '', '/asistente'); return f; })
      .then((f) => setConfirmar({
        candidatos: [{ ...IA.candidatoDeFicha(f), detalle: 'abierto desde el panel' }],
        vinculado: null,
        mensaje: tarea
          ? `Vas a redactar: ${tarea}. Revisa que este sea el caso correcto antes de empezar.`
          : 'Vas a trabajar sobre este caso del panel. Revisa que sea el correcto.',
        pregunta: '',
        desdePanel: true,
        tarea: tarea || undefined,
      }))
      .catch(() => setError('No se pudo abrir el caso del panel. Puedes vincularlo desde "Vincular caso".'));
  }, [tieneAcceso, sinPanel]);

  // El desplazamiento sigue también al texto que crece: si no, el escrito se
  // escribe fuera de la pantalla y hay que perseguirlo con la rueda del mouse.
  // 'auto' y no 'smooth' mientras llega texto, porque con decenas de pedazos
  // por segundo el desplazamiento suave se atropella consigo mismo.
  useEffect(() => { bottomRef.current?.scrollIntoView({ behavior: 'smooth' }); }, [mensajes, cargando]);
  useEffect(() => { if (enVivo?.texto) bottomRef.current?.scrollIntoView({ behavior: 'auto' }); }, [enVivo?.texto]);

  /** `alEntrar`: se está reabriendo sola la última conversación. Si ya no
   *  existe, se empieza en blanco sin mostrar error: nadie pidió abrirla. */
  const abrirConversacion = async (id: string, alEntrar = false) => {
    cerrarSiAngosto();
    setError('');
    setConvActualId(id);
    try {
      const conv = await IA.obtenerConversacion(id);
      setMensajes(conv.mensajes ?? []);
      setDocumentos(conv.documentos ?? []);
      setCaso(conv.caso ?? null);
      setCasoPerdido(false);
      // Se abre una conversación cuya respuesta todavía se está escribiendo:
      // típico al volver al teléfono y encontrar la página recargada. Se
      // espera acá mismo en vez de dejarla como si no hubiera pasado nada.
      if (conv.enCurso && !cargando) {
        setCargando(true);
        setEsperando(true);
        try {
          const teniamos = (conv.mensajes ?? []).filter(m => m.rol === 'assistant').length;
          if ((await esperarRespuesta(id, teniamos)) === 'perdida') setError('La consulta en curso no alcanzó a terminar. Puedes enviarla de nuevo.');
        } finally {
          setCargando(false);
          setEsperando(false);
        }
      }
    } catch {
      if (alEntrar) { if (convActualRef.current === id) setConvActualId(null); return; }
      setError('No se pudo abrir la conversación.');
    }
  };

  // Al entrar, se vuelve a la conversación reciente —ver CLAVE_CONV_ABIERTA—,
  // salvo que se llegue desde un caso del panel: eso abre su propio diálogo.
  useEffect(() => {
    if (!tieneAcceso || new URLSearchParams(window.location.search).get('caso')) return;
    const id = conversacionReciente();
    if (id) void abrirConversacion(id, true);
    // Solo al entrar: abrirConversacion cambia en cada render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tieneAcceso]);

  const nuevaConversacion = () => {
    cerrarSiAngosto();
    setConvActualId(null);
    setMensajes([]);
    setDocumentos([]);
    setCaso(null);
    setCasoPerdido(false);
    setInput('');
    setError('');
  };

  /** Abre la conversación si todavía no existe, y devuelve su id. Vincular un
   *  caso o adjuntar necesitan una conversación donde colgarse. Mira la
   *  referencia, no el estado: puede llamarse dos veces en el mismo cierre. */
  const asegurarConversacion = async (): Promise<string> => {
    if (convActualRef.current) return convActualRef.current;
    const id = (await IA.crearConversacion()).id;
    setConvActualId(id);
    return id;
  };

  /** Vincular (o soltar) el caso de la conversación abierta. Con `leerDocs`,
   *  además adjunta los documentos del caso: es lo que hace que Chatbularis
   *  pueda leer lo que el cliente entregó, no solo saber que existe.
   *
   *  Devuelve el aviso que haya que mostrar (documentos que no se pudieron
   *  leer, los que no cupieron), o '' si todo salió bien. Lo devuelve además
   *  de dejarlo en `error`, porque quien llama desde un diálogo tiene que
   *  mostrarlo dentro del diálogo: el aviso de la página queda tapado. */
  const cambiarCaso = async (casoId: string | null, leerDocs = false): Promise<string> => {
    setError('');
    try {
      const id = await asegurarConversacion();
      const r = await IA.vincularCaso(id, casoId);
      setCaso(r.caso);
      setCasoPerdido(false);
      // Al cambiar de caso el servidor quita los adjuntos del anterior.
      setDocumentos(r.documentos ?? []);
      const aviso = casoId && leerDocs ? await leerDocumentosDelCaso(id, casoId, r.documentos ?? []) : '';
      await recargarHistorial();
      if (aviso) setError(aviso);
      return aviso;
    } catch (e: any) {
      const m = e?.message ?? 'No se pudo vincular el caso.';
      setError(m);
      return m;
    }
  };

  /** Adjunta a la conversación los documentos del caso que todavía no están,
   *  uno por uno y con progreso: una foto o un escaneo se transcriben y eso
   *  toma segundos por archivo. Hasta el tope de archivos por consulta; si el
   *  caso tiene más, se dice cuántos quedaron fuera. Lo que falle no detiene
   *  a los demás. Devuelve el aviso a mostrar ('' si no hay nada que decir);
   *  quien llama decide dónde mostrarlo. */
  const leerDocumentosDelCaso = async (convId: string, casoId: string, actuales: IA.DocumentoAdjunto[]): Promise<string> => {
    let ficha: IA.CasoFicha;
    try { ficha = await IA.fichaCaso(casoId); } catch { return 'No se pudieron listar los documentos del caso.'; }
    // Por id del documento y no por nombre: dos "Liquidación.pdf" son dos.
    const yaEstan = new Set(actuales.map(d => d.documentoId).filter(Boolean));
    const aLeer = ficha.documentos.filter(d => !yaEstan.has(d.id));
    if (!aLeer.length) return '';
    // TODOS los documentos del caso: ya no hay un tope de diez. Cada pedido
    // vuelve al instante —el servidor lee en segundo plano, o reutiliza una
    // lectura anterior—, así que se mandan de a cuatro y el avance de la
    // lectura en sí se ve en cada archivo ("leyendo…").
    const problemas: string[] = [];
    let hechos = 0;
    let sinCuota = false;
    setSubiendo({ hecho: 0, total: aLeer.length });
    const cola = [...aLeer];
    const obrero = async () => {
      while (cola.length && !sinCuota) {
        const d = cola.shift()!;
        try {
          const doc = await IA.adjuntarDocumentoDelCaso(convId, d.id);
          setDocumentos(ds => (ds.some(x => x.id === doc.id) ? ds : [...ds, doc]));
        } catch (err: any) {
          problemas.push(`"${d.nombre}": ${err?.message ?? 'no se pudo leer'}`);
          // Sin cuota no tiene sentido seguir intentando con los demás.
          if (IA.sinSaldo(err)) { sinCuota = true; setVerSaldo(true); }
        }
        setSubiendo({ hecho: ++hechos, total: aLeer.length });
      }
    };
    await Promise.all(Array.from({ length: Math.min(4, aLeer.length) }, obrero));
    setSubiendo(null);
    void recargarSaldo();
    return problemas.join(' ');
  };

  /** Mientras haya adjuntos leyéndose en el servidor, se pregunta cada pocos
   *  segundos cómo van. Un expediente escaneado largo son minutos; la pantalla
   *  sigue usable y cada archivo muestra su estado. */
  const leyendo = documentos.filter(d => d.estado === 'leyendo').length;
  const conProblemas = documentos.filter(d => d.estado === 'error' || d.estado === 'parcial').length;
  useEffect(() => {
    if (!leyendo || !convActualId) return;
    const id = convActualId;
    const t = setInterval(async () => {
      try {
        const conv = await IA.obtenerConversacion(id);
        if (convActualRef.current !== id) return;
        setDocumentos(conv.documentos ?? []);
        if (!(conv.documentos ?? []).some(d => d.estado === 'leyendo')) void recargarSaldo();
      } catch { /* se reintenta en la próxima vuelta */ }
    }, 3000);
    return () => clearInterval(t);
  }, [leyendo, convActualId, recargarSaldo]);

  const reintentarAdjunto = async (doc: IA.DocumentoAdjunto) => {
    if (!convActualId) return;
    setError('');
    try {
      const nuevo = await IA.reintentarDocumento(convActualId, doc.id);
      setDocumentos(ds => [...ds.filter(x => x.id !== doc.id), nuevo]);
    } catch (e: any) {
      setError(e?.message ?? 'No se pudo volver a leer.');
      if (IA.sinSaldo(e)) setVerSaldo(true);
    }
  };

  const eliminar = async (id: string, e: React.MouseEvent) => {
    e.stopPropagation();
    try {
      await IA.eliminarConversacion(id);
      if (id === convActualId) nuevaConversacion();
      await recargarHistorial();
    } catch {
      setError('No se pudo eliminar la conversación.');
    }
  };

  const enviar = () => enviarPregunta(input.trim(), {});

  /** Manda la pregunta. `decision` viene del diálogo de confirmación de caso:
   *  el caso que el abogado confirmó, o que esta pregunta no es de ninguno. */
  const enviarPregunta = async (pregunta: string, decision: { casoId?: string; sinCaso?: boolean }) => {
    // Un error de un paso anterior (documentos que no se leyeron) se conserva
    // y se vuelve a mostrar al final: la respuesta no lo borra.
    // El error de una consulta anterior que falló no es un aviso: con esta
    // respuesta queda resuelto y no se vuelve a mostrar.
    const avisoPrevio = error !== errorDeConsulta.current ? error : '';
    const teniamos = mensajes.filter(m => m.rol === 'assistant').length;
    // Con una subida en curso no se envía: el asistente vería solo los
    // archivos que alcanzaron a quedar guardados y respondería sobre la mitad
    // del material sin decir que le faltaba el resto.
    if (!pregunta || cargando || subiendo) return;
    setError('');
    setCargando(true);
    const soltarPantalla = mantenerPantallaEncendida();
    // La pregunta se pinta de inmediato: la respuesta puede tardar minutos y
    // ver el propio mensaje en pantalla confirma que se envió.
    setMensajes(m => [...m, { rol: 'user', texto: pregunta, ts: new Date().toISOString() }]);
    setInput('');

    // La conversación se abre ANTES de preguntar, aunque el servidor sepa
    // crearla solo. Es lo que hace posible rescatar la respuesta si la conexión
    // se corta: sin id no hay dónde ir a buscarla, y el escrito —ya escrito, ya
    // cobrado— queda en la base sin que nadie sepa que está ahí. Abrir la
    // conversación no llama al modelo: no cuesta nada.
    let convId = convActualRef.current;
    try {
      if (!convId) {
        convId = (await IA.crearConversacion()).id;
        setConvActualId(convId);
      }
    } catch {
      // Si ni siquiera se puede abrir la conversación, se sigue igual: el
      // servidor la creará. Se pierde el rescate, no la consulta.
    }

    setEnVivo({ texto: '', segundos: 0 });
    try {
      const r = await IA.consultar(pregunta, convId ?? undefined, razonamiento, {
        onPensando: (segundos) => setEnVivo(v => (v ? { ...v, segundos } : { texto: '', segundos })),
        onTexto: (pedazo) => setEnVivo(v => ({ texto: (v?.texto ?? '') + pedazo, segundos: v?.segundos ?? 0 })),
      }, decision);
      setConvActualId(r.conversacionId);
      if (r.caso !== undefined) setCaso(r.caso);
      setCasoPerdido(!!r.casoPerdido);
      // El razonamiento sale de la RESPUESTA y no del estado de la pantalla:
      // si alguien cambia el selector mientras el modelo escribe, la marca
      // tiene que decir con cuál se generó, no con cuál se generaría ahora.
      setMensajes(m => [...m, { rol: 'assistant', texto: r.respuesta, fuentes: r.fuentes, razonamiento: r.razonamiento, ...(r.noLeidos?.length ? { noLeidos: r.noLeidos } : {}), ...(r.cobradoClp != null ? { cobradoClp: r.cobradoClp } : {}), ts: new Date().toISOString() }]);
      setAvisoSaldo(
        r.saldo?.porAgotarse
          ? `A tu estudio le quedan ${clp(r.saldo.saldoClp)} de saldo de Chatbularis. Conviene cargar antes de quedarse a mitad de un escrito.`
          : '',
      );
      await recargarHistorial();
      // El saldo se vuelve a pedir porque la consulta que acaba de responder ya
      // está cobrada: dejar el pie con la cifra anterior haría dudar de todas.
      void recargarSaldo();
      if (avisoPrevio) setError(avisoPrevio);
    } catch (e: any) {
      // El servidor no respondió: pide confirmar de qué caso del panel se
      // trata. No se cobró nada. Se saca la pregunta de la pantalla —vuelve
      // sola cuando el abogado decida— y se abre el diálogo.
      const pide = IA.pideConfirmarCaso(e);
      if (pide) {
        setMensajes(m => m.slice(0, -1));
        setConfirmar({ ...pide, pregunta });
        return;
      }

      // ANTES de dar la consulta por perdida, hay que ir a buscarla.
      //
      // El servidor sigue trabajando aunque el navegador se desconecte, y
      // guarda el escrito en la conversación antes de cobrarlo. En el teléfono
      // la conexión se corta apenas se bloquea la pantalla o se cambia de
      // aplicación, y el modelo puede tardar minutos más en terminar.
      //
      // Antes se miraba UNA vez, en el instante del corte: la respuesta todavía
      // no estaba, la pantalla mostraba "Load failed" y el abogado reenviaba
      // la pregunta. Pasó el 27-sep-2026 y se cobraron dos demandas idénticas.
      // Ahora se espera mientras el servidor diga que la consulta sigue en
      // curso. El 409 de "ya hay una en curso" se espera igual: esa es la
      // respuesta que vale.
      if (convId && (IA.seCortoLaConexion(e) || IA.hayConsultaEnCurso(e))) {
        setEsperando(true);
        const r = await esperarRespuesta(convId, teniamos);
        if (r !== 'perdida') return;
      }

      // Se devuelve el texto al cuadro para no perder lo escrito, incluso si
      // lo que pasó es que caducó la clave y hay que volver a entrar.
      setMensajes(m => m.slice(0, -1));
      setInput(pregunta);
      const m = IA.seCortoLaConexion(e) || IA.hayConsultaEnCurso(e)
        ? 'Se cortó la conexión y la respuesta no alcanzó a guardarse. Tu pregunta quedó en el cuadro para enviarla de nuevo.'
        : e?.message ?? 'El asistente no pudo responder. Intenta de nuevo.';
      errorDeConsulta.current = m;
      setError(m);
      // Si lo que pasó es que no alcanza el saldo, el pie tiene que reflejarlo
      // de inmediato: el mensaje de error explica qué pasó, y el saldo al lado
      // es lo que lo hace creíble.
      if (IA.sinSaldo(e)) {
        void recargarSaldo();
        // Se abre el diálogo para cargar directamente: quien acaba de chocar
        // con el saldo ya sabe qué quiere hacer, y hacerlo buscar el botón es
        // hacerlo esperar dos veces.
        setVerSaldo(true);
      }
    } finally {
      setCargando(false);
      setEnVivo(null);
      setEsperando(false);
      void soltarPantalla.then(soltar => soltar());
    }
  };

  const adjuntar = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const elegidos = Array.from(e.target.files ?? []);
    e.target.value = '';
    if (!elegidos.length || subiendo) return;
    setError('');

    // Todo lo que no se pueda subir se junta acá y se avisa junto al final: si
    // se cortara en el primer archivo malo, los otros ocho no se subirían.
    const problemas: string[] = [];
    const cupo = IA.MAX_DOCS - documentos.length;
    let candidatos = elegidos;
    if (candidatos.length > cupo) {
      problemas.push(`Una consulta admite hasta ${IA.MAX_DOCS} archivos: se omitieron ${candidatos.length - cupo}.`);
      candidatos = candidatos.slice(0, Math.max(cupo, 0));
    }
    const validos = candidatos.filter(f => {
      const motivo = IA.revisarArchivo(f);
      if (motivo) problemas.push(motivo);
      return !motivo;
    });
    if (!validos.length) { setError(problemas.join(' ')); return; }

    setSubiendo({ hecho: 0, total: validos.length });
    try {
      // El adjunto cuelga de una conversación, así que si es la primera vez
      // hay que abrirla antes. Se abre vacía: el título lo pone la primera
      // pregunta de verdad.
      let id = convActualId;
      if (!id) {
        id = (await IA.crearConversacion()).id;
        setConvActualId(id);
      }
      // Uno por uno y en orden: así el abogado ve aparecer los archivos a
      // medida que quedan listos, y el que falle no arrastra a los demás.
      for (let i = 0; i < validos.length; i++) {
        try {
          const doc = await IA.adjuntarDocumento(id, validos[i]);
          setDocumentos(d => [...d, doc]);
        } catch (err: any) {
          problemas.push(`"${validos[i].name}": ${err?.message ?? 'no se pudo leer'}`);
          // Sin saldo no tiene sentido seguir con los demás.
          if (IA.sinSaldo(err)) { setVerSaldo(true); break; }
        }
        setSubiendo({ hecho: i + 1, total: validos.length });
      }
      await recargarHistorial();
      // Leer una foto o un PDF escaneado lo transcribe el modelo, y eso se
      // cobra: el saldo del pie tiene que moverse al adjuntar, no solo al
      // preguntar.
      void recargarSaldo();
    } catch (err: any) {
      problemas.push(err?.message ?? 'No se pudieron adjuntar los archivos.');
    } finally {
      setSubiendo(null);
      setError(problemas.join(' '));
    }
  };

  /** Espera en la base la respuesta que el navegador no alcanzó a recibir.
   *
   *  Pregunta cada pocos segundos —y de inmediato al volver a la pestaña— y,
   *  cuando la encuentra, la pinta como si hubiera llegado normalmente: para
   *  el abogado no hubo error, solo demora. Deja de esperar cuando el servidor
   *  dice que la consulta ya no está en curso sin haber dejado respuesta: ahí
   *  sí se perdió. Sin red se sigue intentando; es justo lo que pasa mientras
   *  el teléfono vuelve.
   *
   *  Compara contra las respuestas que había ANTES de preguntar: lo que
   *  confirma que la consulta se completó no es que la conversación exista,
   *  sino que tenga una respuesta más. */
  const esperarRespuesta = useCallback(async (id: string, teniamos: number): Promise<'lista' | 'perdida' | 'otra'> => {
    const limite = Date.now() + 20 * 60 * 1000;
    for (;;) {
      if (convActualRef.current !== id) return 'otra';
      try {
        const conv = await IA.obtenerConversacion(id);
        if (convActualRef.current !== id) return 'otra';
        if ((conv.mensajes ?? []).filter(m => m.rol === 'assistant').length > teniamos) {
          setMensajes(conv.mensajes ?? []);
          setDocumentos(conv.documentos ?? []);
          setCaso(conv.caso ?? null);
          await recargarHistorial();
          void recargarSaldo();
          return 'lista';
        }
        if (!conv.enCurso) return 'perdida';
      } catch { /* sin red todavía: se vuelve a intentar */ }
      if (Date.now() > limite) return 'perdida';
      await pausaOVuelta(4000);
    }
  }, [recargarHistorial, recargarSaldo]);

  const elegirRazonamiento = (r: IA.Razonamiento) => {
    setRazonamiento(r);
    IA.guardarRazonamiento(r);
  };

  const quitarAdjunto = async (docId: string) => {
    if (!convActualId) return;
    // Se quita de la pantalla de inmediato y se avisa al servidor después: la
    // espera no aporta nada y el error, si lo hay, se muestra igual.
    setDocumentos(d => d.filter(x => x.id !== docId));
    try {
      await IA.quitarDocumento(convActualId, docId);
      await recargarHistorial();
    } catch {
      setError('No se pudo quitar el archivo.');
    }
  };

  // Sin sesión, Chatbularis tiene su propio ingreso. Antes se mandaba a la
  // pantalla del panel ("Acceso interno"), que para quien solo contrató
  // Chatbularis era la puerta de algo que no tiene; y la app instalada en el
  // teléfono, que abre /asistente, pasaba por ahí cada vez que vencía la sesión.
  if (!perfil) return <IngresoChatbularis />;

  if (bloqueo) {
    return (
      <Bloqueo
        motivo={bloqueo.motivo}
        mensaje={bloqueo.error}
        pago={bloqueo.pago ?? null}
        lecturaHasta={bloqueo.lecturaHasta}
        onSalir={() => { signOut(auth); window.location.href = '/'; }}
      />
    );
  }

  if (!tieneAcceso) {
    return (
      <div className="al-root" style={{ alignItems: 'center', justifyContent: 'center' }}>
        <div style={{ textAlign: 'center', padding: '2rem' }}>
          <div style={{ fontSize: '2.5rem', marginBottom: '1rem' }}>🔒</div>
          <h2 style={{ color: '#111827', fontFamily: 'Cinzel, serif', marginBottom: '0.5rem' }}>{perfil ? 'Acceso restringido' : 'Ingresando…'}</h2>
          <p style={{ color: '#6b7280', fontSize: '0.9rem' }}>
            {perfil ? 'Chatbularis IA está disponible para abogados, procuradores y administración.' : 'Te llevamos a la pantalla de ingreso.'}
          </p>
          {!sinPanel && <a href="/" style={{ display: 'inline-block', marginTop: '1.5rem', color: '#1d4ed8', fontSize: '0.85rem' }}>← Volver al panel</a>}
          <button onClick={() => { signOut(auth); window.location.href = '/asistente'; }} style={{ display: 'block', margin: '1rem auto 0', background: 'none', border: 'none', color: '#6b7280', fontSize: '0.8rem', cursor: 'pointer', textDecoration: 'underline' }}>Entrar con otra cuenta</button>
        </div>
      </div>
    );
  }


  return (
    <div className="al-root">
      <header className="al-header">
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
          <button className="al-sidebar-toggle" onClick={() => setSidebarOpen(o => !o)} title="Historial">☰</button>
          <a href={sinPanel ? '/asistente' : '/'} className="al-logo" title={sinPanel ? undefined : 'Volver al panel'}>
            <LogoChatbularisSVG size={34} />
            <div className="al-logo-text">
              {/* El nombre sale del estudio de la sesión: el asistente es de
                  Tabularis y lo usan estudios distintos. */}
              <span className="al-logo-name">{perfil.estudio || 'Tabularis'}</span>
              <span className="al-logo-tag">Chatbularis IA</span>
            </div>
          </a>
        </div>
        <MenuUsuario
          perfil={perfil}
          sinPanel={sinPanel}
          onEquipo={() => setVerEquipo(true)}
        />
      </header>

      <div className="al-body">
        {sidebarOpen && <div className="al-sidebar-fondo" onClick={() => setSidebarOpen(false)} aria-hidden />}
        <aside className={`al-sidebar${sidebarOpen ? ' al-sidebar--open' : ''}`}>
          <button className="al-new-btn" onClick={nuevaConversacion}>+ Nueva consulta</button>
          <div className="al-conv-list">
            {cargandoHistorial && <p className="al-conv-empty">Cargando historial…</p>}
            {!cargandoHistorial && conversaciones.length === 0 && <p className="al-conv-empty">Sin historial aún</p>}
            {conversaciones.map(conv => (
              <div
                key={conv.id}
                className={`al-conv-item${conv.id === convActualId ? ' al-conv-item--active' : ''}`}
                onClick={() => abrirConversacion(conv.id)}
              >
                {/* Una consulta abierta solo para adjuntar todavía no tiene
                    título: lo pone la primera pregunta. */}
                <div className="al-conv-titulo">{conv.titulo || 'Nueva consulta'}</div>
                <div className="al-conv-meta">
                  {conv.caso && <span className="al-conv-caso" title={`Caso ${conv.caso.codigo} · ${conv.caso.cliente}`}>📁 {conv.caso.codigo}</span>}
                  {conv.documentos.length > 0 && (
                    <span className="al-conv-autor">
                      📎 {conv.documentos.length === 1 ? conv.documentos[0].nombre : `${conv.documentos.length} archivos`}
                    </span>
                  )}
                  <span>{fmtFecha(conv.actualizado)}</span>
                </div>
                <button className="al-conv-delete" onClick={e => eliminar(conv.id, e)} title="Eliminar">×</button>
              </div>
            ))}
          </div>
          {/* La biblioteca del estudio: sus modelos y su estilo. Va acá y no
              escondida en Configuración porque es parte del trabajo de
              redactar, y quien la va a cargar es quien está redactando. */}
          <div className="al-biblioteca-pie">
            <div className="al-consumo-titulo">Biblioteca del estudio</div>
            <div className="al-oferta-panel-texto">Tus modelos de demandas y escritos, y cómo redacta tu estudio. Solo los ve tu estudio.</div>
            <button className="al-recargar" onClick={() => setVerBiblioteca(true)}>Modelos y estilo</button>
          </div>
          {saldo && <PieSaldo s={saldo} onSaldo={() => setVerSaldo(true)} />}
          {/* A la vista y no solo en el menú del nombre: el plan es por
              estudio, y un admin que no sabe que puede sumar a su equipo lo
              usa solo. */}
          {sinPanel && perfil.rol === 'admin' && (
            <div className="al-biblioteca-pie">
              <div className="al-consumo-titulo">Tu equipo</div>
              <div className="al-oferta-panel-texto">Invita a quienes redactan en tu estudio. Cada uno con su cuenta; sin costo por persona.</div>
              <button className="al-recargar" onClick={() => setVerEquipo(true)}>Invitar al equipo</button>
            </div>
          )}
          {sinPanel && (
            <div className="al-oferta-panel">
              <div className="al-consumo-titulo">¿Y las causas?</div>
              <div className="al-oferta-panel-texto">
                Tabularis también lleva tus casos: etapas, cuotas con recordatorio al cliente, plazos legales y audiencias.
              </div>
              <button className="al-recargar" onClick={() => setVerPanel(true)}>Conocer el panel de gestión</button>
            </div>
          )}
        </aside>

        <main className="al-main">
          <div className="al-scroll-area">
            {vacio ? (
              <div className="al-hero">
                <div className="al-hero-icon"><LogoChatbularisSVG size={72} /></div>
                <h1 className="al-title">Chatbularis IA</h1>
                <p className="al-subtitle">
                  Describe el caso y redactaré el escrito jurídico fundamentado<br />
                  en la base documental y en fuentes oficiales.
                </p>
                <InstalarApp />
                <button className="al-ejemplos-toggle" onClick={() => setMostrarEjemplos(v => !v)}>
                  {mostrarEjemplos ? '▲ Ocultar ejemplos' : '▼ Ver ejemplos de casos'}
                </button>
                {mostrarEjemplos && (
                  <div className="al-examples">
                    {EJEMPLOS.map((ex, i) => (
                      <button key={i} className="al-example-btn" onClick={() => { setInput(ex); setMostrarEjemplos(false); }}>{ex}</button>
                    ))}
                  </div>
                )}
              </div>
            ) : (
              <div className="al-chat">
                {mensajes.map((m, i) => (
                  <div key={i} className={`al-msg al-msg-${m.rol}`}>
                    <div className="al-msg-bubble">
                      {/* Preguntada desde Word sobre un texto seleccionado:
                          sin él, "corrige esto" no se entiende al releerla. */}
                      {m.seleccion && <blockquote className="al-msg-seleccion" title="Texto seleccionado en Word">{m.seleccion}</blockquote>}
                      {!!m.documentoWord && <div className="al-msg-seleccion">Preguntada desde Word, con el documento completo ({m.documentoWord.toLocaleString('es-CL')} caracteres).</div>}
                      <div className="al-msg-text"><ReactMarkdown remarkPlugins={[remarkGfm]}>{m.texto}</ReactMarkdown></div>
                      {/* Con cuál se redactó. Leyendo un escrito de hace tres
                          semanas, saberlo cambia cuánto hay que revisarlo. Las
                          respuestas anteriores a que se pudiera elegir no lo
                          traen y van sin marca. */}
                      {m.rol === 'assistant' && m.razonamiento && (
                        <div className="al-msg-modelo">
                          {textoRazonamiento(m.razonamiento)}
                          {/* Lo que costó, a la vista: con prepago, el abogado
                              aprende rápido qué consultas salen caras. */}
                          {m.cobradoClp != null && <span className="al-msg-costo"> · Esta consulta: {clp(m.cobradoClp)}</span>}
                        </div>
                      )}
                      {m.fuentes && m.fuentes.length > 0 && (
                        <div className="al-fuentes">
                          <span className="al-fuentes-label">Fuentes consultadas:</span>
                          {m.fuentes.map((f, j) => (
                            <span key={j} className={`al-fuente-chip${f.startsWith(IA.PREFIJO_FUENTE_ESTUDIO) ? ' al-fuente-chip--estudio' : ''}`}>{f}</span>
                          ))}
                        </div>
                      )}
                      {/* Lo que el asistente no pudo leer para ESTA respuesta. No
                          va en el Word ni en el PDF: es para el abogado. */}
                      {m.rol === 'assistant' && m.noLeidos && m.noLeidos.length > 0 && (
                        <div className="al-no-leidos">
                          <div className="al-no-leidos-titulo">
                            {m.noLeidos.length === 1 ? 'Un documento no se pudo leer entero para esta respuesta' : `${m.noLeidos.length} documentos no se pudieron leer enteros para esta respuesta`}
                          </div>
                          <ul>
                            {m.noLeidos.map((n, j) => <li key={j}><strong>{n.nombre}</strong>: {n.motivo}</li>)}
                          </ul>
                        </div>
                      )}
                      {m.rol === 'assistant' && pidioArchivo(mensajes[i - 1]) && <BarraDescarga texto={m.texto} opciones={{ ...(formato ?? IA.FORMATO_ESCRITO_DEFAULT), estudio: perfil.estudio }} />}
                    </div>
                  </div>
                ))}
                {cargando && (
                  <div className="al-msg al-msg-assistant">
                    <div className="al-msg-bubble">
                      {enVivo?.texto ? (
                        <>
                          {/* Lo que va escribiendo. Se pinta en markdown igual
                              que una respuesta terminada, así el escrito no
                              cambia de aspecto al completarse. */}
                          <div className="al-msg-text">
                            <ReactMarkdown remarkPlugins={[remarkGfm]}>{enVivo.texto}</ReactMarkdown>
                          </div>
                          <span className="al-cursor" aria-hidden />
                        </>
                      ) : (
                        /* Todavía no escribe nada. Con razonamiento adaptativo
                           esto puede durar medio minuto, y sin los segundos a la
                           vista parece que se colgó. */
                        <div className="al-pensando">
                          <span className="al-pensando-punto" />
                          <span>Analizando{enVivo && enVivo.segundos > 4 ? ` · ${enVivo.segundos}s` : '…'}</span>
                        </div>
                      )}
                    </div>
                  </div>
                )}
                <div ref={bottomRef} />
              </div>
            )}
            {esperando && (
              <div className="al-aviso">
                Se cortó la conexión, pero Chatbularis sigue trabajando en tu consulta. La respuesta aparecerá aquí apenas termine; no hace falta enviarla de nuevo.
              </div>
            )}
            {pagoVuelta && <ResumenPago v={pagoVuelta} onCerrar={cerrarPagoVuelta} />}
            {avisoSaldo && <div className="al-aviso">{avisoSaldo}</div>}
            {error && <div className="al-error">{error}</div>}
          </div>

          <div className="al-input-area">
            {/* El caso del panel sobre el que se trabaja. Sin panel no hay
                casos que vincular y el botón sobra. */}
            {(!sinPanel || documentos.length > 0 || subiendo) && (
              <div className="al-caso-bar">
                {/* El caso del panel sobre el que se trabaja. Sin panel no hay
                    casos que vincular y el botón sobra. */}
                {!sinPanel && (caso ? (
                  <span className="al-caso-chip" title={`La ficha del caso ${caso.codigo} (partes, RUT, hechos, rol, etapa) va en cada consulta de esta conversación.`}>
                    📁 <strong>{caso.codigo}</strong> <span className="al-caso-chip-cliente">· {caso.cliente}</span>
                    <button type="button" onClick={() => setVerCaso(true)} title="Ver la ficha y adjuntar documentos del caso">Ver</button>
                    <button type="button" onClick={() => cambiarCaso(null)} title="Seguir esta conversación sin el caso">✕</button>
                  </span>
                ) : (
                  <button type="button" className="al-caso-btn" onClick={() => setVerCaso(true)} disabled={cargando}
                    title="Busca un caso del panel por nombre, RUT, código o rol. El asistente recibe su ficha en cada consulta y puede adjuntar sus documentos.">
                    📁 Vincular caso
                  </button>
                ))}
                {(documentos.length > 0 || subiendo) && (
                  <button type="button" className="al-docs-toggle" onClick={alternarDocs} aria-expanded={docsAbiertos}
                    title={docsAbiertos ? 'Ocultar los documentos' : 'Ver los documentos'}>
                    📎 {documentos.length}<span className="al-docs-largo"> {documentos.length === 1 ? 'documento' : 'documentos'}</span>
                    {leyendo > 0 && <span className="al-docs-marca al-docs-marca--leyendo">⏳ {leyendo}<span className="al-docs-largo"> leyendo</span></span>}
                    {conProblemas > 0 && <span className="al-docs-marca al-docs-marca--problema">⚠ {conProblemas}<span className="al-docs-largo"> con problemas</span></span>}
                    {subiendo && <span className="al-docs-marca al-docs-marca--leyendo">adjuntando {Math.min(subiendo.hecho + 1, subiendo.total)}/{subiendo.total}</span>}
                    <span className={`al-docs-flecha${docsAbiertos ? ' al-docs-flecha--abierta' : ''}`} aria-hidden>▾</span>
                  </button>
                )}
              </div>
            )}
            {!sinPanel && casoPerdido && <div className="al-caso-perdido">⚠️ El caso vinculado ya no se puede ver (está en la papelera o dejó de ser tuyo): la última respuesta salió sin su ficha.</div>}
            {docsAbiertos && (documentos.length > 0 || subiendo) && (
              <div className="al-attach-bar">
                {documentos.map(doc => {
                  const estado = doc.estado ?? 'leido';
                  const icono = estado === 'leyendo' ? '⏳' : estado === 'error' ? '⚠️' : estado === 'parcial' ? '◐' : doc.origen === 'texto' ? '📄' : '🖼️';
                  const titulo =
                    estado === 'leyendo' ? `${doc.nombre} · leyendo…`
                      : estado === 'error' ? `${doc.nombre} · no se pudo leer: ${doc.motivo ?? ''}`
                        : `${doc.nombre} · ${doc.caracteres.toLocaleString('es-CL')} caracteres${doc.paginas ? ` · ${doc.paginas} páginas` : ''}` +
                          (doc.origen === 'texto' ? '' : ' · incluye texto transcrito de imágenes, revísalo') +
                          (estado === 'parcial' ? ` · lectura parcial: ${doc.motivo ?? ''}` : doc.motivo ? ` · ${doc.motivo}` : '');
                  return (
                    <span key={doc.id} className={`al-attach-chip al-attach-chip--${estado}`} title={titulo}>
                      {icono} {doc.nombre}{estado === 'leyendo' ? ' · leyendo…' : ''}
                      {estado === 'error' && doc.documentoId && (
                        <button className="al-attach-chip-reintentar" onClick={() => reintentarAdjunto(doc)} title="Volver a leerlo">Reintentar</button>
                      )}
                      <button className="al-attach-chip-remove" onClick={() => quitarAdjunto(doc.id)} title="Quitar archivo">✕</button>
                    </span>
                  );
                })}
                {subiendo && (
                  <span className="al-attach-chip al-attach-chip--cargando">
                    ⏳ Adjuntando {Math.min(subiendo.hecho + 1, subiendo.total)} de {subiendo.total}…
                  </span>
                )}
              </div>
            )}
            {/* Se puede preguntar mientras se lee, pero hay que saberlo: la
                respuesta no tendrá en cuenta lo que todavía se está leyendo
                (y lo dirá al pie). */}
            {leyendo > 0 && (
              <div className="al-leyendo-aviso">
                {leyendo === 1 ? 'Un documento se está leyendo' : `${leyendo} documentos se están leyendo`}. Un escaneo largo puede tomar unos minutos; si preguntas antes, la respuesta no {leyendo === 1 ? 'lo' : 'los'} tendrá en cuenta.
              </div>
            )}
            {/* La elección va PEGADA al cuadro de texto y no en un menú de
                ajustes: se decide por consulta, mirando lo que se va a pedir,
                no una vez y para siempre. */}
            <div className="al-razonamiento" role="radiogroup" aria-label="Modo">
              <span className="al-razonamiento-label">Modo</span>
              <div className="al-razonamiento-opciones">
                {IA.RAZONAMIENTOS.map(r => (
                  <button
                    key={r.id}
                    type="button"
                    role="radio"
                    aria-checked={razonamiento === r.id}
                    className={`al-razonamiento-btn${razonamiento === r.id ? ' al-razonamiento-btn--activo' : ''}`}
                    onClick={() => elegirRazonamiento(r.id)}
                    title={r.detalle}
                  >
                    <span className="al-razonamiento-nombre">{r.etiqueta}</span>
                    <span className="al-razonamiento-modelo">
                      {r.modelo}
                      {/* El precio típico al lado del modo: se elige sabiendo
                          cuánto cuesta. Sin tope no se muestra: no se cobra. */}
                      {saldo?.precios?.[r.id] && !saldo.sinTope ? ` · ≈ ${clp(saldo.precios[r.id].tipicoClp)}` : ''}
                    </span>
                  </button>
                ))}
              </div>
              {/* La recomendación en una línea: sin ella, el nombre del modo
                  solo no dice para qué tarea conviene. */}
              <span className="al-razonamiento-hint">
                {IA.RAZONAMIENTOS.find(r => r.id === razonamiento)?.para}
              </span>
            </div>
            <textarea
              ref={textareaRef}
              className="al-textarea"
              placeholder={esAngosto()
                ? (documentos.length ? '¿Qué hago con estos documentos?' : 'Describe el caso o tu consulta…')
                : documentos.length
                ? 'Indícale a la IA qué quieres: corrección, puntos débiles, artículos aplicables, comparar los documentos…'
                : 'Describe el caso: partes, hechos, pretensión, monto, antecedentes relevantes…'}
              value={input}
              onChange={e => setInput(e.target.value)}
              onKeyDown={e => { if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) enviar(); }}
              rows={2}
            />
            <div className="al-input-footer">
              <button
                className="al-attach-btn"
                onClick={() => fileRefChat.current?.click()}
                disabled={!!subiendo || documentos.length >= IA.MAX_DOCS}
                title={
                  documentos.length >= IA.MAX_DOCS
                    ? `Máximo ${IA.MAX_DOCS} archivos por consulta`
                    : `Escritos, documentos y fotos (PDF, Word, .txt, JPG, PNG). Hasta ${IA.MAX_ARCHIVO_MB} MB por archivo (${IA.MAX_IMAGEN_MB} MB las imágenes); puedes elegir varios de una vez.`
                }
              >
                {subiendo ? `⏳ ${subiendo.hecho}/${subiendo.total}` : <>📎<span className="al-attach-btn-texto"> Adjuntar archivos</span></>}
              </button>
              <input
                ref={fileRefChat}
                type="file"
                multiple
                accept={IA.ACCEPT_ADJUNTOS}
                onChange={adjuntar}
                style={{ display: 'none' }}
              />
              {/* De las fotos se lee el texto, no se "ven": conviene decirlo,
                  porque cambia lo que tiene sentido adjuntar. */}
              <span className="al-input-hint">
                {documentos.length > 0
                  ? `${documentos.length} ${documentos.length === 1 ? 'archivo' : 'archivos'} · Ctrl+Enter para enviar`
                  : 'PDF, Word, Excel, correos y fotos (se lee su texto) · Ctrl+Enter para enviar'}
              </span>
              <button className="al-send-btn" onClick={enviar} disabled={!input.trim() || cargando || !!subiendo}>
                {cargando ? 'Procesando…' : subiendo ? 'Leyendo archivos…' : 'Enviar →'}
              </button>
            </div>
          </div>
        </main>
      </div>

      {verSaldo && saldo && <DialogoSaldo s={saldo} onCerrar={() => { setVerSaldo(false); if (window.location.search) window.history.replaceState(null, '', '/asistente'); }} />}
      {verPanel && perfil && <DialogoPanel esAdmin={perfil.rol === 'admin'} onCerrar={() => setVerPanel(false)} />}
      {verEquipo && <DialogoEquipo yo={perfil.uid} onCerrar={() => setVerEquipo(false)} />}
      {confirmar && (
        <DialogoConfirmarCaso
          c={confirmar}
          onElegir={async (id, leerDocs) => {
            const { pregunta, desdePanel } = confirmar;
            setConfirmar(null);
            if (pregunta) {
              // Primero se vincula y se leen los documentos, y recién entonces
              // va la pregunta: así la primera respuesta ya sale con lo que el
              // cliente entregó, no solo con la ficha. El aviso de la lectura
              // (si hubo) queda en pantalla después de la respuesta.
              if (leerDocs) await cambiarCaso(id, true);
              await enviarPregunta(pregunta, { casoId: id });
            } else {
              await cambiarCaso(id, leerDocs);
              // La tarea elegida en la ficha queda escrita como primera
              // consulta: el abogado la completa o la manda tal cual.
              if (confirmar.tarea) {
                const t = confirmar.tarea;
                setInput(`Redacta para este caso, con los datos de la ficha: ${t}. `);
              }
              if (desdePanel) setVerCaso(true);
            }
          }}
          onSinCaso={async () => {
            const { pregunta } = confirmar;
            setConfirmar(null);
            if (pregunta) await enviarPregunta(pregunta, { sinCaso: true });
          }}
          onCerrar={() => {
            // Cancelar: la pregunta vuelve al cuadro, sin enviar.
            if (confirmar.pregunta) setInput(confirmar.pregunta);
            setConfirmar(null);
          }}
        />
      )}
      {verCaso && (
        <DialogoCaso
          caso={caso}
          documentos={documentos}
          onVincular={(id, leerDocs) => cambiarCaso(id, leerDocs)}
          onAdjuntarTodos={async () => {
            if (!caso) return '';
            const id = await asegurarConversacion();
            const aviso = await leerDocumentosDelCaso(id, caso.id, documentosRef.current);
            await recargarHistorial();
            if (aviso) setError(aviso);
            return aviso;
          }}
          onQuitar={async () => { await cambiarCaso(null); setVerCaso(false); }}
          onAdjuntar={async (documentoId) => {
            const id = await asegurarConversacion();
            const doc = await IA.adjuntarDocumentoDelCaso(id, documentoId);
            setDocumentos(d => [...d, doc]);
            await recargarHistorial();
            // Leer un escaneo se transcribe con el modelo y descuenta saldo.
            void recargarSaldo();
          }}
          onCerrar={() => setVerCaso(false)}
        />
      )}
      {verBiblioteca && <DialogoBiblioteca onCerrar={() => setVerBiblioteca(false)} onSaldo={() => void recargarSaldo()} onFormato={setFormato} />}
    </div>
  );
};

/** Una tarjeta de caso: lo justo para reconocerlo sin abrir el panel. */
const TarjetaCaso: React.FC<{ c: IA.CasoCandidato; activa?: boolean; accion?: string; onElegir?: () => void; cargando?: boolean }> = ({ c, activa, accion, onElegir, cargando }) => (
  <div className={`al-caso-tarjeta${activa ? ' al-caso-tarjeta--activa' : ''}`} onClick={onElegir} role={onElegir ? 'button' : undefined}>
    <div className="al-caso-tarjeta-nombre">{c.cliente}</div>
    {/* "por nombre Pérez" cuando lo propuso el servidor; "abierto desde el
        panel" cuando vino de la ficha, sin el "por". */}
    {c.detalle && <span className="al-caso-tarjeta-motivo">{c.motivo ? `por ${c.detalle}` : c.detalle}</span>}
    <div className="al-caso-tarjeta-datos">
      <code>{c.codigo}</code>
      {c.rut && <> · RUT {c.rut}</>}
      {c.tema && <> · {c.tema}</>}
      {c.estado && <> · {c.estado}</>}
      {c.rolCaso && <><br />Rol: <code>{c.rolCaso}</code></>}
      {c.contraparte && <><br />Contraparte: {c.contraparte}</>}
      {c.parteRepresentada && <> · {c.parteRepresentada}</>}
      {c.abogada && <><br />Abogado: {c.abogada}</>}
      {typeof c.documentos === 'number' && <><br />📄 {c.documentos === 0 ? 'Sin documentos guardados' : c.documentos === 1 ? '1 documento guardado' : `${c.documentos} documentos guardados`}</>}
    </div>
    {accion && onElegir && (
      <button type="button" className="al-caso-tarjeta-elegir" disabled={cargando} onClick={(e) => { e.stopPropagation(); onElegir(); }}>
        {cargando ? 'Un momento…' : accion}
      </button>
    )}
  </div>
);

/** "Leer también los documentos del caso". Una sola, para los dos diálogos
 *  que vinculan: el texto de la cuota es del tipo que se ajusta después, y
 *  tiene que cambiar en los dos a la vez. */
const CasillaLeerDocs: React.FC<{ checked: boolean; onChange: (v: boolean) => void }> = ({ checked, onChange }) => (
  <label className="al-leer-docs">
    <input type="checkbox" checked={checked} onChange={e => onChange(e.target.checked)} />
    <span>Leer también los documentos del caso: se adjuntan a esta consulta. Las fotos y escaneos se transcriben y descuentan del saldo.</span>
  </label>
);

/** "¿Te refieres a este caso?"
 *
 *  Aparece cuando la pregunta nombra un caso del panel y la conversación no
 *  tiene uno (o nombra uno distinto). No se responde hasta que el abogado
 *  diga cuál es, o diga que ninguno: dos clientes con el mismo apellido son
 *  lo normal, y una demanda con los datos del cliente equivocado es el peor
 *  error que puede cometer el asistente. No cuesta una consulta. */
const DialogoConfirmarCaso: React.FC<{
  c: IA.ConfirmacionCaso & { pregunta: string; desdePanel?: boolean };
  onElegir: (casoId: string, leerDocs: boolean) => Promise<void>;
  onSinCaso: () => Promise<void>;
  onCerrar: () => void;
}> = ({ c, onElegir, onSinCaso, onCerrar }) => {
  const [eligiendo, setEligiendo] = useState<string | null>(null);
  /** Leer también los documentos del caso. Marcado por omisión: para
   *  redactar sobre un caso, lo que el cliente entregó es tan necesario como
   *  la ficha. Se puede desmarcar para no gastar saldo transcribiendo fotos. */
  const [leerDocs, setLeerDocs] = useState(true);
  const conDocs = c.candidatos.some(x => (x.documentos ?? 0) > 0);
  const elegir = async (id: string) => { if (eligiendo) return; setEligiendo(id); try { await onElegir(id, leerDocs && conDocs); } finally { setEligiendo(null); } };
  const sinCaso = async () => { if (eligiendo) return; setEligiendo('ninguno'); try { await onSinCaso(); } finally { setEligiendo(null); } };
  const varios = c.candidatos.length > 1;
  return (
    <div className="al-modal" onClick={onCerrar}>
      <div className="al-modal-caja al-modal-caja--ancha" onClick={(e) => e.stopPropagation()}>
        <div className="al-modal-cabecera">
          <h2>{c.desdePanel ? (c.tarea ? `Redactar: ${c.tarea}` : '¿Es este el caso?') : varios ? '¿De cuál caso se trata?' : '¿Te refieres a este caso?'}</h2>
          <button onClick={onCerrar} title="Cerrar">×</button>
        </div>
        <p className="al-modal-intro">
          {c.desdePanel
            ? c.mensaje
            : c.vinculado
              ? `Esta consulta está vinculada al caso ${c.vinculado.codigo} (${c.vinculado.cliente}), pero la pregunta nombra otro. Elige con cuál seguir; el que elijas queda vinculado.`
              : varios
                ? 'La pregunta calza con más de un caso del panel. Elige el correcto y el asistente recibirá su ficha: partes, RUT, hechos, rol y etapa.'
                : 'La pregunta parece hablar de este caso del panel. Si es él, el asistente recibirá su ficha: partes, RUT, hechos, rol y etapa.'}
        </p>
        <div className="al-casos-lista">
          {c.candidatos.map((cand) => (
            <TarjetaCaso key={cand.id} c={cand} accion={varios ? 'Es este' : 'Sí, es este caso'} onElegir={() => elegir(cand.id)} cargando={eligiendo === cand.id} />
          ))}
        </div>
        {conDocs && <CasillaLeerDocs checked={leerDocs} onChange={setLeerDocs} />}
        <div className="al-modal-acciones">
          <button type="button" className="al-btn-secundario" onClick={onCerrar} disabled={!!eligiendo}>Cancelar</button>
          {!c.desdePanel && (
            <button type="button" className="al-btn-secundario" onClick={sinCaso} disabled={!!eligiendo}>
              {c.vinculado ? `Seguir con ${c.vinculado.codigo}` : 'No, no es de ningún caso'}
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

/** Buscar y vincular un caso del panel, ver su ficha y adjuntar sus documentos.
 *
 *  Con caso vinculado muestra la ficha y los documentos; sin caso, el
 *  buscador. Elegir uno lo vincula de inmediato: acá el abogado ya está
 *  mirando cliente, RUT y código, que es toda la confirmación que hace falta. */
const DialogoCaso: React.FC<{
  caso: IA.CasoVinculado | null;
  documentos: IA.DocumentoAdjunto[];
  /** Devuelven el aviso a mostrar dentro del diálogo ('' si nada). */
  onVincular: (casoId: string, leerDocs: boolean) => Promise<string>;
  onQuitar: () => Promise<void>;
  onAdjuntar: (documentoId: string) => Promise<void>;
  onAdjuntarTodos: () => Promise<string>;
  onCerrar: () => void;
}> = ({ caso, documentos, onVincular, onQuitar, onAdjuntar, onAdjuntarTodos, onCerrar }) => {
  const [q, setQ] = useState('');
  const [leerDocs, setLeerDocs] = useState(true);
  const [resultados, setResultados] = useState<IA.CasoCandidato[]>([]);
  const [buscando, setBuscando] = useState(false);
  const [ficha, setFicha] = useState<IA.CasoFicha | null>(null);
  const [ocupado, setOcupado] = useState<string | null>(null);
  const [error, setError] = useState('');

  // La ficha del caso vinculado, con sus documentos.
  useEffect(() => {
    if (!caso) { setFicha(null); return; }
    IA.fichaCaso(caso.id).then(setFicha).catch(() => setError('No se pudo cargar la ficha del caso.'));
  }, [caso]);

  // Buscar mientras se escribe, con una pausa corta para no pedir por cada
  // letra.
  useEffect(() => {
    if (caso) return;
    const texto = q.trim();
    if (texto.length < 2) { setResultados([]); return; }
    setBuscando(true);
    const t = setTimeout(() => {
      IA.buscarCasos(texto).then(setResultados).catch(() => setResultados([])).finally(() => setBuscando(false));
    }, 250);
    return () => clearTimeout(t);
  }, [q, caso]);

  const vincular = async (id: string) => {
    if (ocupado) return;
    setOcupado(id); setError('');
    try { const aviso = await onVincular(id, leerDocs); setQ(''); setResultados([]); if (aviso) setError(aviso); } catch (e: any) { setError(e?.message ?? 'No se pudo vincular.'); } finally { setOcupado(null); }
  };
  const adjuntar = async (docId: string) => {
    if (ocupado) return;
    setOcupado(docId); setError('');
    try { await onAdjuntar(docId); } catch (e: any) { setError(e?.message ?? 'No se pudo adjuntar.'); } finally { setOcupado(null); }
  };
  const yaAdjunto = (docId: string) => documentos.some(d => d.documentoId === docId);
  const faltan = ficha ? ficha.documentos.filter(d => !yaAdjunto(d.id)).length : 0;
  const adjuntarTodos = async () => {
    if (ocupado) return;
    setOcupado('todos'); setError('');
    try { const aviso = await onAdjuntarTodos(); if (aviso) setError(aviso); } catch (e: any) { setError(e?.message ?? 'No se pudieron adjuntar.'); } finally { setOcupado(null); }
  };

  return (
    <div className="al-modal" onClick={onCerrar}>
      <div className="al-modal-caja al-modal-caja--ancha" onClick={(e) => e.stopPropagation()}>
        <div className="al-modal-cabecera">
          <h2>{caso ? `Caso ${caso.codigo}` : 'Vincular un caso del panel'}</h2>
          <button onClick={onCerrar} title="Cerrar">×</button>
        </div>
        {caso ? (
          <>
            <p className="al-modal-intro">
              La ficha de este caso (partes, RUT, domicilios, hechos, rol de la causa, etapa y audiencias) va en cada consulta de esta conversación. Sin honorarios ni cuotas.
            </p>
            {ficha ? (
              <TarjetaCaso c={IA.candidatoDeFicha(ficha)} activa />
            ) : !error && <p className="al-modal-nota">Cargando la ficha…</p>}
            {ficha && (
              <div className="al-caso-docs">
                <div className="al-caso-docs-cabecera">
                  <div className="al-caso-docs-titulo">Documentos del caso</div>
                  {faltan > 0 && (
                    <button type="button" className="al-btn-secundario" disabled={!!ocupado || documentos.length >= IA.MAX_DOCS} onClick={adjuntarTodos}>
                      {ocupado === 'todos' ? 'Leyendo…' : faltan === 1 ? 'Leer el que falta' : `Leer los ${faltan} que faltan`}
                    </button>
                  )}
                </div>
                {ficha.documentos.length === 0 && <p className="al-modal-nota" style={{ margin: 0 }}>Este caso no tiene documentos guardados.</p>}
                {ficha.documentos.map(d => (
                  <div key={d.id} className="al-caso-doc">
                    <span title={d.nombre}>📄 {d.nombre}</span>
                    <button type="button" disabled={!!ocupado || yaAdjunto(d.id) || documentos.length >= IA.MAX_DOCS} onClick={() => adjuntar(d.id)}>
                      {ocupado === d.id ? 'Leyendo…' : yaAdjunto(d.id) ? 'Adjunto' : 'Adjuntar a la consulta'}
                    </button>
                  </div>
                ))}
                <p className="al-modal-nota">Un documento adjunto se lee completo; si es una foto o un escaneo se transcribe, y eso descuenta del saldo como cualquier adjunto.</p>
              </div>
            )}
            <div className="al-modal-acciones">
              <button type="button" className="al-btn-secundario" onClick={onQuitar} disabled={!!ocupado}>Quitar el caso de esta consulta</button>
              <button type="button" className="al-plan-elegir" style={{ width: 'auto' }} onClick={onCerrar}>Listo</button>
            </div>
          </>
        ) : (
          <>
            <p className="al-modal-intro">
              Busca por nombre del cliente, RUT, código del caso (por ejemplo DEM-Q5VM7A) o rol de la causa. Al elegirlo, el asistente recibe su ficha en cada consulta y lee los documentos que el cliente entregó.
            </p>
            <input className="al-buscador-casos" autoFocus placeholder="Nombre, RUT, código o rol…" value={q} onChange={(e) => setQ(e.target.value)} />
            <CasillaLeerDocs checked={leerDocs} onChange={setLeerDocs} />
            <div className="al-casos-lista">
              {buscando && resultados.length === 0 && <p className="al-modal-nota">Buscando…</p>}
              {!buscando && q.trim().length >= 2 && resultados.length === 0 && <p className="al-modal-nota">Ningún caso calza con "{q.trim()}".</p>}
              {resultados.map(c => <TarjetaCaso key={c.id} c={c} accion="Usar este caso" onElegir={() => vincular(c.id)} cargando={ocupado === c.id} />)}
            </div>
          </>
        )}
        {error && <div className="al-error" style={{ margin: '8px 0 0' }}>{error}</div>}
      </div>
    </div>
  );
};

/** La biblioteca del estudio: sus modelos de escritos y su estilo.
 *
 *  Lo que se sube queda en el estudio y solo en él (aislado por estudio en la
 *  base y en el almacenamiento), y no entrena a ningún modelo: en cada
 *  consulta del estudio, el asistente recibe los trozos de estos documentos
 *  que se parecen a lo que se pide, como referencia de forma. Se dice tal cual
 *  en pantalla, porque "alimentar la IA" suena a que el material se va a
 *  algún lado. */
const DialogoBiblioteca: React.FC<{ onCerrar: () => void; onSaldo: () => void; onFormato: (f: IA.FormatoEstudio) => void }> = ({ onCerrar, onSaldo, onFormato }) => {
  const [b, setB] = useState<IA.Biblioteca | null>(null);
  const [estilo, setEstilo] = useState('');
  const [guardando, setGuardando] = useState(false);
  const [guardado, setGuardado] = useState(false);
  const [formato, setFormato] = useState<IA.FormatoEstudio | null>(null);
  const [guardandoFormato, setGuardandoFormato] = useState(false);
  const [formatoListo, setFormatoListo] = useState(false);
  const [subiendo, setSubiendo] = useState<{ hecho: number; total: number } | null>(null);
  const [error, setError] = useState('');
  const fileRef = useRef<HTMLInputElement>(null);

  const recargar = useCallback(async () => {
    try {
      const r = await IA.listarBiblioteca();
      setB(r);
      return r;
    } catch (e: any) {
      setError(e?.message ?? 'No se pudo cargar la biblioteca.');
      return null;
    }
  }, []);

  useEffect(() => { recargar().then(r => { if (r) { setEstilo(r.estilo); setFormato(r.formato); } }); }, [recargar]);

  const cambiarFormato = async (parche: Partial<IA.FormatoEstudio>) => {
    if (!formato || guardandoFormato) return;
    const nuevo = { ...formato, ...parche };
    setFormato(nuevo); setGuardandoFormato(true); setError(''); setFormatoListo(false);
    try {
      const r = await IA.guardarFormato(nuevo);
      setFormato(r.chatbularis.formato);
      // La página lo usa al armar las descargas: se le pasa directo.
      onFormato(r.chatbularis.formato);
      setFormatoListo(true);
    } catch (e: any) { setError(e?.message ?? 'No se pudo guardar el formato.'); } finally { setGuardandoFormato(false); }
  };

  // Mientras haya documentos indexándose, se vuelve a mirar cada pocos
  // segundos: indexar toma minutos y el estado tiene que moverse solo.
  useEffect(() => {
    if (!b?.documentos.some(d => d.estado === 'procesando')) return;
    const t = setInterval(() => void recargar(), 5000);
    return () => clearInterval(t);
  }, [b, recargar]);

  const guardarEstilo = async () => {
    if (guardando) return;
    setGuardando(true); setError(''); setGuardado(false);
    try {
      const r = await IA.guardarEstilo(estilo);
      setGuardado(true);
      setB(prev => (prev ? { ...prev, estilo: r.chatbularis.estilo } : prev));
    } catch (e: any) { setError(e?.message ?? 'No se pudo guardar el estilo.'); } finally { setGuardando(false); }
  };

  const subir = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const elegidos = Array.from(e.target.files ?? []);
    e.target.value = '';
    if (!elegidos.length || subiendo) return;
    setError('');
    const problemas: string[] = [];
    const validos = elegidos.filter(f => { const m = IA.revisarArchivo(f); if (m) problemas.push(m); return !m; });
    setSubiendo({ hecho: 0, total: validos.length });
    try {
      for (let i = 0; i < validos.length; i++) {
        try {
          const d = await IA.subirABiblioteca(validos[i]);
          if (d.recortado) problemas.push(`"${validos[i].name}" es muy largo: se guardaron los primeros ${(b?.maxCaracteres ?? 300000).toLocaleString('es-CL')} caracteres.`);
        } catch (err: any) { problemas.push(`"${validos[i].name}": ${err?.message ?? 'no se pudo subir'}`); }
        setSubiendo({ hecho: i + 1, total: validos.length });
      }
    } finally {
      setSubiendo(null);
      setError(problemas.join(' '));
      await recargar();
      onSaldo();
    }
  };

  const eliminar = async (d: IA.DocumentoBiblioteca) => {
    if (!window.confirm(`¿Quitar "${d.nombre}" de la biblioteca? El asistente dejará de usarlo como referencia.`)) return;
    setError('');
    try { await IA.eliminarDeBiblioteca(d.id); await recargar(); } catch (e: any) { setError(e?.message ?? 'No se pudo quitar.'); }
  };

  const [reintentando, setReintentando] = useState<string | null>(null);
  const reintentar = async (d: IA.DocumentoBiblioteca) => {
    if (reintentando) return;
    setReintentando(d.id); setError('');
    try { await IA.reindexarBiblioteca(d.id); await recargar(); } catch (e: any) { setError(e?.message ?? 'No se pudo reintentar.'); } finally { setReintentando(null); }
  };

  /** Una indexación que lleva más de lo que puede durar se quedó a medias
   *  (el servidor se reinició con ella en el aire): se ofrece reintentar. */
  const atascado = (d: IA.DocumentoBiblioteca) =>
    d.estado === 'procesando' && !!b && Date.now() - new Date(d.creado).getTime() > b.indexacionMaxMs;
  const estadoTexto = (d: IA.DocumentoBiblioteca) => d.estado === 'listo' ? 'Listo' : d.estado === 'error' ? 'Falló' : atascado(d) ? 'Interrumpida' : 'Indexando…';

  return (
    <div className="al-modal" onClick={onCerrar}>
      <div className="al-modal-caja al-modal-caja--ancha" onClick={(e) => e.stopPropagation()}>
        <div className="al-modal-cabecera">
          <h2>Biblioteca del estudio</h2>
          <button onClick={onCerrar} title="Cerrar">×</button>
        </div>
        <p className="al-modal-intro">
          Tus propios modelos de demandas, contestaciones y escritos, y las instrucciones de cómo redacta tu estudio. Con esto, Chatbularis redacta con la forma de tu estudio y no con una genérica.
        </p>

        <div className="al-seccion-titulo">Cómo redacta el estudio</div>
        <p className="al-seccion-nota">
          Encabezados, fórmulas, cómo se numeran las peticiones, cómo se firma, tono. Va en cada consulta del estudio.
          {b && !b.puedeEditarEstilo && ' Lo edita quien administra el estudio.'}
        </p>
        <textarea
          className="al-biblioteca-estilo"
          value={estilo}
          onChange={(e) => { setEstilo(e.target.value.slice(0, b?.maxEstilo ?? 4000)); setGuardado(false); }}
          disabled={!b || !b.puedeEditarEstilo}
          placeholder={'Ej.: Encabezamos con "S.J.L. del Trabajo de Santiago". Las peticiones van numeradas en romanos. Citamos los artículos con su texto entre comillas. Firmamos como "Estudio Jurídico Pérez & Asociados".'}
        />
        {b?.puedeEditarEstilo && (
          <div className="al-modal-acciones" style={{ marginTop: 6 }}>
            <span className="al-seccion-nota" style={{ marginRight: 'auto' }}>{estilo.length} / {b.maxEstilo}</span>
            {guardado && <span className="al-seccion-nota" style={{ color: '#166534' }}>Guardado.</span>}
            <button type="button" className="al-plan-elegir" style={{ width: 'auto' }} onClick={guardarEstilo} disabled={guardando || estilo === (b?.estilo ?? '')}>
              {guardando ? 'Guardando…' : 'Guardar estilo'}
            </button>
          </div>
        )}

        <div className="al-seccion-titulo">Formato de los archivos</div>
        <p className="al-seccion-nota">
          Con esto se arman el Word y el PDF que descargas: letra, tamaño, interlineado y márgenes. Lo que va en negrita, cómo se titulan las secciones o cómo se numera va en las instrucciones de arriba, que el asistente sigue al escribir.
          {b && !b.puedeEditarEstilo && ' Lo cambia quien administra el estudio.'}
        </p>
        {formato && (
          <div className="al-formato">
            <label>Letra
              <select value={formato.fuente} disabled={!b?.puedeEditarEstilo || guardandoFormato} onChange={e => cambiarFormato({ fuente: e.target.value as IA.FormatoEstudio['fuente'] })}>
                {IA.FUENTES_ESCRITO.map(f => <option key={f} value={f}>{f}</option>)}
              </select>
            </label>
            <label>Tamaño
              <select value={formato.tamano} disabled={!b?.puedeEditarEstilo || guardandoFormato} onChange={e => cambiarFormato({ tamano: Number(e.target.value) as IA.FormatoEstudio['tamano'] })}>
                {IA.TAMANOS_ESCRITO.map(t => <option key={t} value={t}>{t} pt</option>)}
              </select>
            </label>
            <label>Interlineado
              <select value={formato.interlineado} disabled={!b?.puedeEditarEstilo || guardandoFormato} onChange={e => cambiarFormato({ interlineado: Number(e.target.value) as IA.FormatoEstudio['interlineado'] })}>
                {IA.INTERLINEADOS_ESCRITO.map(t => <option key={t} value={t}>{t === 1 ? 'Sencillo' : t === 2 ? 'Doble' : String(t).replace('.', ',')}</option>)}
              </select>
            </label>
            <label>Márgenes
              <select value={formato.margenCm} disabled={!b?.puedeEditarEstilo || guardandoFormato} onChange={e => cambiarFormato({ margenCm: Number(e.target.value) as IA.FormatoEstudio['margenCm'] })}>
                {IA.MARGENES_ESCRITO.map(t => <option key={t} value={t}>{String(t).replace('.', ',')} cm</option>)}
              </select>
            </label>
            <span className="al-seccion-nota" style={{ margin: 0, alignSelf: 'center' }}>{guardandoFormato ? 'Guardando…' : formatoListo ? 'Guardado.' : ''}</span>
            {/* El PDF se arma con las letras que trae el generador (una serif y
                una sans); la exacta va solo en el Word. Se dice acá y no se
                descubre abriendo el archivo. */}
            <span className="al-seccion-nota" style={{ flexBasis: '100%', margin: 0 }}>
              El Word lleva {formato.fuente}; el PDF se arma con {familiaPdf(formato.fuente) === 'times' ? 'Times' : 'Helvetica'}, la letra más parecida disponible.
            </span>
          </div>
        )}

        <div className="al-seccion-titulo">Modelos y escritos</div>
        <p className="al-seccion-nota">
          PDF, Word o texto. Se leen, se trocean y en cada consulta el asistente recibe los trozos que se parecen a lo que se pide, como referencia de forma. Los datos de esos escritos (nombres, RUT, hechos) no se copian al caso actual.
          {b && ` ${b.documentos.length} de ${b.maxDocumentos}.`}
        </p>
        {b?.puedeEditar && (
          <div className="al-modal-acciones" style={{ justifyContent: 'flex-start', marginTop: 4 }}>
            <button type="button" className="al-btn-secundario" onClick={() => fileRef.current?.click()} disabled={!!subiendo || (b ? b.documentos.length >= b.maxDocumentos : true)}>
              {subiendo ? `⏳ Subiendo ${subiendo.hecho}/${subiendo.total}…` : '📎 Subir modelos'}
            </button>
            <input ref={fileRef} type="file" multiple accept={IA.ACCEPT_ADJUNTOS} onChange={subir} style={{ display: 'none' }} />
          </div>
        )}
        <div className="al-biblioteca-lista">
          {b && b.documentos.length === 0 && <p className="al-modal-nota" style={{ margin: '6px 0' }}>Todavía no hay modelos. Sube una demanda o un escrito que represente cómo escribe tu estudio.</p>}
          {b?.documentos.map(d => (
            <div key={d.id} className="al-biblioteca-doc">
              <span className="al-biblioteca-doc-nombre" title={d.nombre}>{d.origen === 'texto' ? '📄' : '🖼️'} {d.nombre}</span>
              <span className={`al-biblioteca-estado al-biblioteca-estado--${d.estado === 'listo' ? 'listo' : d.estado === 'error' ? 'error' : 'procesando'}`} title={d.error ?? ''}>{estadoTexto(d)}</span>
              {b.puedeEditar ? <button type="button" onClick={() => eliminar(d)} title="Quitar de la biblioteca">×</button> : <span />}
              <span className="al-biblioteca-doc-meta">
                {d.caracteres.toLocaleString('es-CL')} caracteres · {fmtFecha(d.creado)}
                {d.estado === 'error' && d.error && <> · {d.error}</>}
                {(d.estado === 'error' || atascado(d)) && b.puedeEditar && <> · <a href="#reintentar" onClick={(e) => { e.preventDefault(); reintentar(d); }}>reintentar</a></>}
              </span>
            </div>
          ))}
        </div>

        <div className="al-biblioteca-privacidad">
          <strong>Protección de datos.</strong> Lo que subes acá queda aislado en tu estudio: ningún otro estudio lo ve ni lo recibe en sus consultas, y no pasa a la base jurídica de Tabularis. No se usa para entrenar ningún modelo: se consulta como referencia en cada respuesta y se borra cuando lo quitas o cuando se borra el estudio.
        </div>
        {error && <div className="al-error" style={{ margin: '8px 0 0' }}>{error}</div>}
      </div>
    </div>
  );
};

export default AsistenteLegal;

/** Ofrece tener Chatbularis como app en el teléfono. Solo en el teléfono o la
 *  tablet, solo si todavía no se está usando como app, y hasta que la persona
 *  diga que no. En Android abre el diálogo del navegador; en el iPhone, donde
 *  Apple no deja ofrecer un botón, explica el camino. Ver utils/instalarApp. */
const InstalarApp: React.FC = () => {
  const [, refrescar] = useState(0);
  const [descartada, setDescartada] = useState(() => {
    try { return localStorage.getItem(CLAVE_APP_DESCARTADA) === '1'; } catch { return false; }
  });
  useEffect(() => App.alCambiarInstalacion(() => refrescar(n => n + 1)), []);

  if (descartada || App.esAppInstalada()) return null;
  const conBoton = App.puedeInstalarConBoton();
  const ios = App.esIOS();
  if (!conBoton && !ios && !App.esAndroid()) return null;

  const descartar = () => {
    setDescartada(true);
    try { localStorage.setItem(CLAVE_APP_DESCARTADA, '1'); } catch { /* sin almacenamiento */ }
  };

  return (
    <div className="al-instalar">
      <div className="al-instalar-texto">
        <strong>Ten Chatbularis como app</strong>
        {conBoton ? (
          <span>Se abre con un toque desde tu pantalla de inicio, a pantalla completa.</span>
        ) : ios ? (
          <span>Toca <b>Compartir</b> <span aria-hidden>⬆︎</span> y luego <b>«Agregar a inicio»</b>. Queda con su ícono, a pantalla completa.</span>
        ) : (
          <span>Abre el menú <b>⋮</b> del navegador y elige <b>«Instalar app»</b> o <b>«Agregar a la pantalla principal»</b>.</span>
        )}
      </div>
      <div className="al-instalar-acciones">
        {conBoton && <button className="al-instalar-btn" onClick={() => void App.instalar()}>Instalar</button>}
        <button className="al-instalar-cerrar" onClick={descartar} title="No mostrar más" aria-label="No mostrar más">×</button>
      </div>
    </div>
  );
};

/** Ingreso propio de Chatbularis, en /asistente del subdominio del estudio.
 *  Es la puerta de quien solo contrató Chatbularis y de la app instalada en el
 *  teléfono; quien tiene el panel también puede entrar por acá. Viene con el
 *  correo puesto cuando llega del registro o de una invitación (?email=). */
const IngresoChatbularis: React.FC = () => {
  const [email, setEmail] = useState(() => {
    try { return new URLSearchParams(window.location.search).get('email') || ''; } catch { return ''; }
  });
  const [clave, setClave] = useState('');
  const [error, setError] = useState('');
  const [entrando, setEntrando] = useState(false);
  const [olvido, setOlvido] = useState<'' | 'abierto' | 'enviado'>('');

  const entrar = async (e: React.FormEvent) => {
    e.preventDefault();
    if (entrando) return;
    setError(''); setEntrando(true);
    try {
      await signInWithEmailAndPassword(auth, email.trim(), clave);
      // Se recarga en vez de re-dibujar: así parte limpio (sin ?email=) y
      // con todo lo que el asistente carga al abrir.
      window.location.replace('/asistente');
    } catch (err: any) {
      setError(err?.message || 'No pudimos ingresar. Revisa tu correo y contraseña.');
      setEntrando(false);
    }
  };
  const recuperar = async () => {
    if (!email.trim()) { setError('Escribe tu correo arriba y te mandamos el enlace.'); return; }
    setError('');
    try { await solicitarReset(email.trim()); } catch { /* la respuesta es la misma exista o no */ }
    setOlvido('enviado');
  };

  return (
    <div className="al-ingreso">
      <form className="al-ingreso-caja" onSubmit={entrar}>
        <div className="al-ingreso-marca">
          <LogoChatbularisSVG size={56} />
          <div>
            <div className="al-ingreso-nombre">Chatbularis</div>
            <div className="al-ingreso-estudio">{TENANT}.tabularis.cl</div>
          </div>
        </div>
        <h1>Ingresa a Chatbularis</h1>
        <label className="al-ingreso-campo">
          <span>Correo</span>
          <input type="email" value={email} onChange={e => setEmail(e.target.value)} placeholder="tu@correo.cl" autoComplete="username" required autoFocus={!email} />
        </label>
        <label className="al-ingreso-campo">
          <span>Contraseña</span>
          <input type="password" value={clave} onChange={e => setClave(e.target.value)} placeholder="••••••••" autoComplete="current-password" required autoFocus={!!email} />
        </label>
        {error && <div className="al-ingreso-error">{error}</div>}
        <button type="submit" className="al-ingreso-boton" disabled={entrando}>{entrando ? 'Ingresando…' : 'Ingresar →'}</button>
        {olvido === 'enviado'
          ? <div className="al-ingreso-nota">Si el correo es de este estudio, te mandamos un enlace para crear una contraseña nueva.</div>
          : olvido === 'abierto'
            ? <button type="button" className="al-ingreso-link" onClick={recuperar}>Enviarme el enlace a {email.trim() || 'mi correo'}</button>
            : <button type="button" className="al-ingreso-link" onClick={() => setOlvido('abierto')}>¿Olvidaste tu contraseña?</button>}
        <div className="al-ingreso-pie">
          ¿Te invitaron y aún no tienes contraseña? Búscala en el correo de invitación.
          <br />¿Tu estudio usa el panel de gestión? <a href="/">Ingresa al panel</a>.
        </div>
      </form>
    </div>
  );
};

const iniciales = (nombre: string) =>
  nombre.trim().split(/\s+/).slice(0, 2).map(p => p[0]?.toUpperCase() ?? '').join('') || '?';

/** Nombre de quien tiene la sesión abierta, arriba a la derecha. En un estudio
 *  varias personas usan Chatbularis desde el mismo computador; sin esto no hay
 *  cómo saber con qué cuenta se está redactando (y el historial es por
 *  persona). Al tocarlo: equipo del estudio y cerrar sesión. */
const MenuUsuario: React.FC<{ perfil: Perfil; sinPanel: boolean; onEquipo: () => void }> = ({ perfil, sinPanel, onEquipo }) => {
  const [abierto, setAbierto] = useState(false);
  const esAdmin = perfil.rol === 'admin';
  useEffect(() => {
    if (!abierto) return;
    const cerrar = () => setAbierto(false);
    const esc = (e: KeyboardEvent) => { if (e.key === 'Escape') setAbierto(false); };
    document.addEventListener('click', cerrar);
    document.addEventListener('keydown', esc);
    return () => { document.removeEventListener('click', cerrar); document.removeEventListener('keydown', esc); };
  }, [abierto]);
  const salir = () => { signOut(auth); window.location.href = '/asistente'; };

  return (
    <div className="al-usuario" onClick={e => e.stopPropagation()}>
      <button className="al-usuario-boton" onClick={() => setAbierto(a => !a)} aria-expanded={abierto} title={perfil.email}>
        <span className="al-usuario-avatar" aria-hidden>{iniciales(perfil.nombre)}</span>
        <span className="al-usuario-nombre">{perfil.nombre}</span>
        <span className="al-usuario-flecha" aria-hidden>▾</span>
      </button>
      {abierto && (
        <div className="al-usuario-menu" role="menu">
          <div className="al-usuario-quien">
            <strong>{perfil.nombre}</strong>
            <span>{perfil.email}</span>
            <span>{esAdmin ? 'Administrador' : 'Usuario'} · {perfil.estudio}</span>
          </div>
          {/* Con panel, el equipo se lleva desde Usuarios del panel, donde
              están todos los perfiles (captadores, procuradores, externos). */}
          {sinPanel && esAdmin && <button role="menuitem" onClick={() => { setAbierto(false); onEquipo(); }}>Equipo del estudio</button>}
          {!sinPanel && <a role="menuitem" href="/">Ir al panel de gestión</a>}
          <button role="menuitem" onClick={salir}>Cerrar sesión</button>
        </div>
      )}
    </div>
  );
};

const NOMBRE_ROL: Record<string, string> = {
  ADMIN: 'Administrador', ADMIN_NO_ABOGADO: 'Administrador', ABOGADA: 'Usuario', ABOGADO_ASOCIADO: 'Usuario',
  PROCURADORA: 'Procurador', CAPTADORA: 'Captador', CAPTADORA_GENERAL: 'Captador', ABOGADO_EXTERNO: 'Abogado externo',
};

/** El equipo del estudio que solo tiene Chatbularis. Sin panel no había dónde
 *  sumar a nadie: el plan es por estudio, pero lo usaba una sola cuenta.
 *
 *  Dos perfiles y nada más: Usuario (redacta) y Administrador (además invita
 *  gente y cambia el plan). Los del panel —captadores, procuradores,
 *  externos— no significan nada acá. No hay tope de personas: todos comparten
 *  el saldo prepago del estudio. */
const DialogoEquipo: React.FC<{ yo: string; onCerrar: () => void }> = ({ yo, onCerrar }) => {
  const [lista, setLista] = useState<IA.Integrante[] | null>(null);
  const [nombre, setNombre] = useState('');
  const [email, setEmail] = useState('');
  const [rol, setRol] = useState<'ABOGADA' | 'ADMIN'>('ABOGADA');
  const [enviando, setEnviando] = useState(false);
  const [ocupado, setOcupado] = useState<string | null>(null);
  const [error, setError] = useState('');
  // Resultado de la última invitación: si el correo no salió, el enlace para
  // pasarlo a mano (WhatsApp, otro correo).
  const [aviso, setAviso] = useState<null | { texto: string; enlace?: string }>(null);

  const cargar = useCallback(() => {
    IA.equipo().then(setLista).catch((e: any) => setError(e?.message || 'No se pudo cargar el equipo.'));
  }, []);
  useEffect(() => { cargar(); }, [cargar]);

  const resultado = (quien: string, inv?: IA.Invitacion) => {
    if (!inv) return setAviso({ texto: `Listo: ${quien} ya está en el equipo.` });
    setAviso(inv.enviada
      ? { texto: `Le mandamos la invitación a ${quien}. Tiene 24 horas para crear su contraseña; el correo trae también el enlace para entrar.` }
      : { texto: `No pudimos mandar el correo a ${quien}. Pásale este enlace para que cree su contraseña (vale 24 horas):`, enlace: inv.enlace });
  };

  const invitar = async (e: React.FormEvent) => {
    e.preventDefault();
    if (enviando) return;
    setEnviando(true); setError(''); setAviso(null);
    try {
      const r = await IA.invitar({ nombre: nombre.trim(), email: email.trim().toLowerCase(), rol });
      resultado(r.nombre, r.invitacion);
      setNombre(''); setEmail(''); setRol('ABOGADA');
      cargar();
    } catch (err: any) {
      setError(err?.message || 'No se pudo invitar.');
    } finally {
      setEnviando(false);
    }
  };

  const accion = async (u: IA.Integrante, hacer: () => Promise<unknown>, despues?: (r: any) => void) => {
    setOcupado(u.id); setError(''); setAviso(null);
    try { const r = await hacer(); despues?.(r); cargar(); }
    catch (err: any) { setError(err?.message || 'No se pudo completar.'); }
    finally { setOcupado(null); }
  };

  const activos = lista?.filter(u => u.activo) ?? [];
  const inactivos = lista?.filter(u => !u.activo) ?? [];

  const fila = (u: IA.Integrante) => {
    const esYo = u.id === yo;
    const admin = u.rol === 'ADMIN' || u.rol === 'ADMIN_NO_ABOGADO';
    return (
      <div key={u.id} className={`al-equipo-fila${u.activo ? '' : ' al-equipo-fila--inactiva'}`}>
        <span className="al-usuario-avatar" aria-hidden>{iniciales(u.nombre)}</span>
        <div className="al-equipo-datos">
          <div className="al-equipo-nombre">
            {u.nombre}{esYo && <span className="al-equipo-tu"> (tú)</span>}
          </div>
          <div className="al-equipo-correo">{u.email}</div>
          <div className="al-equipo-etiquetas">
            <span className="al-equipo-rol">{NOMBRE_ROL[u.rol] ?? u.rol}</span>
            {u.activo && u.pendiente && <span className="al-equipo-pendiente">Invitación pendiente</span>}
            {!u.activo && <span className="al-equipo-inactivo">Sin acceso</span>}
          </div>
        </div>
        {!esYo && (
          <div className="al-equipo-acciones">
            {u.activo && u.pendiente && (
              <button disabled={ocupado === u.id} onClick={() => accion(u, () => IA.reenviarInvitacion(u.id), (inv) => resultado(u.nombre, inv))}>Reenviar invitación</button>
            )}
            {u.activo && (u.rol === 'ABOGADA' || admin) && (
              <button disabled={ocupado === u.id} onClick={() => accion(u, () => IA.editarIntegrante(u.id, { rol: admin ? 'ABOGADA' : 'ADMIN' }))}>
                {admin ? 'Quitar administrador' : 'Hacer administrador'}
              </button>
            )}
            {u.activo
              ? <button className="al-equipo-quitar" disabled={ocupado === u.id} onClick={() => {
                  if (window.confirm(`¿Quitarle el acceso a ${u.nombre}? No podrá entrar a Chatbularis. Sus conversaciones no se borran y puedes devolverle el acceso cuando quieras.`)) {
                    void accion(u, () => IA.editarIntegrante(u.id, { activo: false }));
                  }
                }}>Quitar acceso</button>
              : <button disabled={ocupado === u.id} onClick={() => accion(u, () => IA.editarIntegrante(u.id, { activo: true }))}>Devolver acceso</button>}
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="al-modal" onClick={onCerrar}>
      <div className="al-modal-caja al-modal-caja--ancha" onClick={(e) => e.stopPropagation()}>
        <div className="al-modal-cabecera">
          <h2>Equipo del estudio</h2>
          <button onClick={onCerrar} title="Cerrar">×</button>
        </div>
        <p className="al-modal-intro">
          Invita a quienes redactan en tu estudio. Cada uno entra con su correo y su contraseña, y tiene su propio historial; todos usan el saldo del estudio. No hay costo por persona.
        </p>

        <form className="al-equipo-invitar" onSubmit={invitar}>
          <div className="al-consumo-titulo">Invitar a alguien</div>
          <div className="al-equipo-campos">
            <input value={nombre} onChange={e => setNombre(e.target.value)} placeholder="Nombre y apellido" required minLength={2} maxLength={120} />
            <input type="email" value={email} onChange={e => setEmail(e.target.value)} placeholder="correo@estudio.cl" required />
            <select value={rol} onChange={e => setRol(e.target.value as 'ABOGADA' | 'ADMIN')} title="Perfil">
              <option value="ABOGADA">Usuario: redacta y consulta</option>
              <option value="ADMIN">Administrador: además invita y cambia el plan</option>
            </select>
          </div>
          <button type="submit" className="al-plan-elegir" disabled={enviando}>{enviando ? 'Enviando…' : 'Enviar invitación'}</button>
          <div className="al-modal-nota">Le llega un correo para crear su contraseña y el enlace para entrar a Chatbularis.</div>
        </form>

        {aviso && (
          <div className="al-equipo-aviso">
            {aviso.texto}
            {aviso.enlace && (
              <div className="al-equipo-enlace">
                <input readOnly value={aviso.enlace} onFocus={e => e.target.select()} />
                <button onClick={() => { navigator.clipboard?.writeText(aviso.enlace!).catch(() => {}); }}>Copiar</button>
              </div>
            )}
          </div>
        )}
        {error && <div className="al-oferta-error">{error}</div>}

        <div className="al-equipo-lista">
          {lista === null && !error && <p className="al-conv-empty">Cargando…</p>}
          {activos.map(fila)}
          {inactivos.length > 0 && <div className="al-equipo-separador">Sin acceso</div>}
          {inactivos.map(fila)}
        </div>
      </div>
    </div>
  );
};

/** Proponerle el panel de gestión al estudio que solo tiene Chatbularis.
 *
 *  Va acá y no en una landing porque quien ya usa el asistente a diario es
 *  justamente quien más gana llevando también las causas, y mandarlo a
 *  registrarse de nuevo en otra parte es perderlo en el camino. Sumarlo rige
 *  al instante y se cobra con la próxima renovación, junto con Chatbularis. */
const QUE_INCLUYE = [
  ['Casos y etapas', 'Cada causa con su procedimiento, documentos y equipo, y tu cliente la consulta solo con un código.'],
  ['Cuotas de honorarios', 'Tabla de cuotas por caso y recordatorio automático al cliente el mismo día en que vence cada una.'],
  ['Plazos y audiencias', 'Plazos legales calculados desde la fecha de despido, calendario de audiencias y aviso al cliente antes de cada una.'],
  ['Documentos y fotos', 'Sube fotos y archivos, y compílalos como PDF listo para tribunales.'],
  ['Reportes y flujo de caja', 'Qué se cobró, qué falta por cobrar y cómo se reparte, por período.'],
];

const DialogoPanel: React.FC<{ esAdmin: boolean; onCerrar: () => void }> = ({ esAdmin, onCerrar }) => {
  const [plan, setPlan] = useState<IA.PlanEstudio | null>(null);
  const [sumando, setSumando] = useState(false);
  const [error, setError] = useState('');
  useEffect(() => {
    // El precio se pide al servidor, que es quien tiene la tarifa; solo
    // administración puede leerlo, y al resto se le muestra sin cifra.
    if (esAdmin) IA.planDelEstudio().then(setPlan).catch(() => setPlan(null));
  }, [esAdmin]);

  const abogados = plan ? Math.max(plan.cupos, plan.abogadosActivos, 1) : null;
  const mensual = plan && abogados ? plan.precios[abogados - 1] : null;

  const sumar = async () => {
    if (sumando) return;
    const pregunta = mensual
      ? `¿Sumar el panel de gestión? Parte con 14 días de prueba; después, ${clp(mensual)} al mes${abogados && abogados > 1 ? ` (${abogados} abogados)` : ''}.`
      : '¿Sumar el panel de gestión a tu estudio? Parte con 14 días de prueba.';
    if (!window.confirm(pregunta)) return;
    setSumando(true); setError('');
    try {
      await IA.contratarPanel();
      marcarConPanel();
      window.location.href = '/';
    } catch (e: any) {
      setError(e?.message || 'No se pudo sumar el panel. Inténtalo de nuevo.');
      setSumando(false);
    }
  };

  return (
    <div className="al-modal" onClick={onCerrar}>
      <div className="al-modal-caja" onClick={(e) => e.stopPropagation()}>
        <div className="al-modal-cabecera">
          <h2>Panel de gestión de causas</h2>
          <button onClick={onCerrar} title="Cerrar">×</button>
        </div>
        <p className="al-modal-intro">
          Hoy tu estudio usa Chatbularis para redactar. El panel lleva además las causas de punta a punta, y tu equipo entra con las mismas cuentas de siempre.
        </p>
        <ul className="al-oferta-lista">
          {QUE_INCLUYE.map(([t, d]) => (
            <li key={t}><strong>{t}.</strong> {d}</li>
          ))}
        </ul>
        <div className="al-oferta-precio">
          {esAdmin
            ? mensual
              ? <><strong>14 días de prueba</strong>; después, <strong>{clp(mensual)} al mes</strong> para tu estudio{abogados && abogados > 1 ? ` con ${abogados} abogados` : ' con un abogado'}. Cada abogado más se suma desde Configuración. Chatbularis sigue con su saldo, aparte.</>
              : plan === null && !error
                ? 'Parte con 14 días de prueba; después se paga la mensualidad del panel.'
                : 'Consultando el precio…'
            : 'Solo el administrador del estudio puede sumarlo. Pídele que entre acá y lo active.'}
        </div>
        {esAdmin && (
          <button className="al-plan-elegir al-oferta-boton" onClick={sumar} disabled={sumando}>
            {sumando ? 'Sumando…' : 'Sumar el panel a mi estudio'}
          </button>
        )}
        {error && <div className="al-oferta-error">{error}</div>}
        <p className="al-modal-nota">Rige al instante: al confirmar entras al panel con tu misma cuenta, con 14 días de prueba. Chatbularis sigue igual, con su saldo.</p>
      </div>
    </div>
  );
};

/** Saldo de Chatbularis: cargar, cuánto cuesta cada modo y en qué se fue.
 *
 *  Chatbularis es prepago. Cargar es un clic: se elige el monto, se paga en
 *  Banchile Pagos y al volver el saldo ya está (ver lib/banchile.ts en el servidor). Solo la
 *  administración carga: es plata del estudio. */
const DialogoSaldo: React.FC<{ s: IA.EstadoSaldo; onCerrar: () => void }> = ({ s, onCerrar }) => {
  const [cargando, setCargando] = useState<number | null>(null);
  const [error, setError] = useState('');
  const cargar = async (montoClp: number) => {
    if (cargando) return;
    setCargando(montoClp); setError('');
    try {
      const r = await pagarSaldoIa(montoClp);
      // A pagar en Banchile. Al volver, la pantalla confirma la carga sola.
      window.location.href = r.url;
    } catch (e: any) {
      setError(e?.message || 'No pudimos abrir el pago. Inténtalo de nuevo.');
      setCargando(null);
    }
  };
  const modos = IA.RAZONAMIENTOS;
  return (
    <div className="al-modal" onClick={onCerrar}>
      <div className="al-modal-caja al-modal-caja--ancha" onClick={(e) => e.stopPropagation()}>
        <div className="al-modal-cabecera">
          <h2>Saldo de Chatbularis</h2>
          <button onClick={onCerrar} title="Cerrar">×</button>
        </div>

        <div className="al-saldo-actual">
          <span>{s.sinTope ? `Gasto de ${nombreDelMes()}` : 'Tu saldo'}</span>
          <strong className={!s.sinTope && s.agotado ? 'al-saldo-rojo' : ''}>{clp(s.sinTope ? s.gastadoMesClp : s.saldoClp)}</strong>
        </div>
        <p className="al-modal-intro">
          {s.sinTope
            ? 'Tu estudio usa Chatbularis sin tope: cada consulta se descuenta a su costo y nunca se detiene.'
            : 'Cada consulta descuenta lo que cuesta, con IVA incluido, y lo ves bajo la respuesta. Lo que cargas no vence y no hay mensualidad.'}
        </p>

        {!s.sinTope && (
          s.puedeCargar ? (
            <>
              <div className="al-consumo-titulo">Cargar saldo</div>
              <div className="al-cargas">
                {s.cargas.map((c) => (
                  <button key={c.montoClp} className="al-carga" onClick={() => cargar(c.montoClp)} disabled={!!cargando}>
                    <strong>{clp(c.montoClp)} <small>CLP</small></strong>
                    <span>{c.bonoClp > 0 ? `+ ${clp(c.bonoClp)} de regalo` : '\u00a0'}</span>
                    {cargando === c.montoClp && <em>Abriendo el pago…</em>}
                  </button>
                ))}
              </div>
              {error && <div className="al-oferta-error">{error}</div>}
              {/* Antes de mandar a pagar: el monto en pesos chilenos y el logo
                  de Banchile Pagos, como recomienda el banco, para que nadie
                  se sorprenda al cambiar de página. */}
              <div className="al-pago-banchile">
                {/* El logo oficial que entrega Banchile para el Web Checkout. */}
                <img src="/logo-banchilepagos.png" alt="Banchile Pagos" />
                <span>Pagas con débito o crédito en Banchile Pagos, del Banco de Chile. Montos en pesos chilenos (CLP), IVA incluido. El saldo queda disponible apenas se confirma el pago.</span>
              </div>
              {/* Los pagos en línea del estudio, con su estado y autorización:
                  lo que pide Banchile y lo que el admin necesita para cuadrar. */}
              <div style={{ marginTop: 16 }}><HistorialPagos /></div>
            </>
          ) : (
            <p className="al-modal-nota">
              {s.cargaEnLinea
                ? 'Solo el administrador del estudio puede cargar saldo. Pídele que entre a Chatbularis y toque «Cargar saldo».'
                : 'El pago en línea todavía no está disponible. Escríbenos a contacto@tabularis.cl y te cargamos el saldo.'}
            </p>
          )
        )}

        <div className="al-consumo-titulo" style={{ marginTop: 18 }}>Cuánto cuesta una consulta</div>
        <div className="al-precios-modo">
          {modos.map((m) => {
            const p = s.precios?.[m.id];
            if (!p) return null;
            return (
              <div key={m.id} className="al-precio-modo">
                <div><strong>{m.etiqueta}</strong> <span>{m.modelo}</span></div>
                <div className="al-precio-modo-cifra">≈ {clp(p.tipicoClp)}</div>
                <div className="al-precio-modo-rango">normalmente entre {clp(p.desdeClp)} y {clp(p.hastaClp)}</div>
              </div>
            );
          })}
        </div>
        <p className="al-modal-nota">
          Lo que más encarece una consulta es cuánto escribe y razona el modelo, los adjuntos largos y las búsquedas en la web.
          Las fotos y escaneos se transcriben una vez y se cobran aparte, a su costo. Para empezar una consulta se necesitan al menos {clp(s.minimos?.rapido ?? 1000)} de saldo, y {clp(s.minimos?.profundo ?? 3000)} en análisis profundo.
        </p>
      </div>
    </div>
  );
};

/** ¿En esta pregunta el abogado pidió el trabajo en Word, en PDF o para
 *  descargar?
 *
 *  Los botones de descarga salen SOLO en la respuesta a una pregunta así, y no
 *  bajo todas las respuestas: en un chat donde se pregunta "¿qué artículo
 *  aplica?" un botón de Word es ruido, y ponerlo en todas partes hace que
 *  parezca que cada respuesta es un documento. Se mira la pregunta y no la
 *  respuesta porque lo que manda es lo que la persona pidió, no lo que el
 *  modelo dijo. Las palabras son las que alguien escribe cuando quiere el
 *  archivo; "documento" o "archivo" a secas no cuentan, porque se usan para
 *  hablar de los adjuntos. */
const pidioArchivo = (pregunta: IA.MensajeIA | undefined) =>
  !!pregunta && pregunta.rol === 'user' && /\b(word|docx|pdf|descarg\w*|export\w*)\b/i.test(pregunta.texto);

/** Descargar una respuesta como Word o PDF.
 *
 *  Va debajo de la respuesta y no en un menú aparte porque la pregunta es
 *  siempre "quiero ESTE escrito en Word", no "quiero exportar". El archivo se
 *  arma en el navegador a partir del texto (ver utils/exportarEscrito): no hay
 *  llamada al servidor ni al modelo, así que no gasta saldo y no puede fallar
 *  por falta de él. */
const BarraDescarga: React.FC<{ texto: string; opciones: OpcionesEscrito }> = ({ texto, opciones }) => {
  const [generando, setGenerando] = useState<FormatoEscrito | null>(null);
  const [fallo, setFallo] = useState('');

  const descargar = async (formato: FormatoEscrito) => {
    if (generando) return;
    setGenerando(formato);
    setFallo('');
    try {
      await descargarEscrito(texto, formato, opciones);
    } catch {
      // Lo que se muestra es la salida que queda: el texto sigue en pantalla
      // y se puede copiar. Un archivo que no se pudo armar no es un escrito
      // perdido.
      setFallo('No se pudo armar el archivo. Puedes copiar el texto de la respuesta.');
    } finally {
      setGenerando(null);
    }
  };

  return (
    <div className="al-descargas">
      <span className="al-descargas-label">Descargar</span>
      <button type="button" className="al-descargar-btn" disabled={!!generando} onClick={() => descargar('docx')}>
        {generando === 'docx' ? 'Armando…' : 'Word (.docx)'}
      </button>
      <button type="button" className="al-descargar-btn" disabled={!!generando} onClick={() => descargar('pdf')}>
        {generando === 'pdf' ? 'Armando…' : 'PDF'}
      </button>
      {fallo && <span className="al-descargas-fallo">{fallo}</span>}
      {/* Lo que se descarga es el escrito solo, sin las frases del chat, con
          la letra del estudio. Se dice acá para que nadie tenga que abrir el
          archivo a comprobarlo. */}
      <span className="al-descargas-nota">
        Borrador para revisión profesional: revísalo antes de presentarlo. El archivo sale solo con el escrito{opciones.fuente ? `, en ${opciones.fuente} ${opciones.tamano}` : ''}.
      </span>
    </div>
  );
};

/** Saldo del estudio, al pie de la barra lateral.
 *
 *  Responde las dos preguntas sin abrir nada: cuánto le queda y cuánto lleva
 *  gastado este mes. Enterarse de que se acabó cuando el servidor rechaza la
 *  consulta, a mitad de una demanda, es tarde. */
const PieSaldo: React.FC<{ s: IA.EstadoSaldo; onSaldo: () => void }> = ({ s, onSaldo }) => {
  const [verCartola, setVerCartola] = useState(false);
  const [verMeses, setVerMeses] = useState(false);
  const nombreMes = (desdeIso: string) => {
    const t = new Date(desdeIso).toLocaleDateString('es-CL', { month: 'long', year: 'numeric', timeZone: 'America/Santiago' });
    return t.charAt(0).toUpperCase() + t.slice(1);
  };
  const estado = s.agotado ? ' al-consumo--agotada' : s.porAgotarse ? ' al-consumo--poca' : '';
  const consultas = (n: number) => `${n} ${n === 1 ? 'consulta' : 'consultas'}`;
  const mes = s.consultasMes > 0
    ? `${clp(s.gastadoMesClp)} en ${consultas(s.consultasMes)} este mes${s.devueltoMesClp > 0 ? `, con ${clp(s.devueltoMesClp)} devueltos` : ''}.`
    : 'Todavía sin consultas este mes.';

  return (
    <div className={`al-consumo${estado}`}>
      <div className="al-consumo-titulo">{s.sinTope ? `Gasto de ${nombreDelMes()}` : 'Saldo de Chatbularis'}</div>
      <div className="al-consumo-saldo">{clp(s.sinTope ? s.gastadoMesClp : s.saldoClp)}</div>
      <div className="al-consumo-pie">
        {s.sinTope
          ? `${mes} Sin tope: se descuenta al costo y no se detiene.`
          : s.agotado
            ? 'No alcanza para una consulta nueva. Carga saldo para seguir.'
            : !s.puede.profundo
              ? `${mes} Alcanza para Redactar y Conversar; el análisis profundo necesita ${clp(s.minimos?.profundo ?? 3000)}.`
              : mes}
      </div>

      {/* Cargar tiene que estar acá, junto al saldo: el momento en que alguien
          quiere cargar es el momento en que ve que no le queda. */}
      {!s.sinTope && (
        <button className={`al-recargar${s.agotado || s.porAgotarse ? ' al-recargar--urgente' : ''}`} onClick={onSaldo}>
          {s.puedeCargar ? 'Cargar saldo' : 'Ver saldo y precios'}
        </button>
      )}

      {(s.historial?.length ?? 0) > 0 && (
        <>
          <button className="al-consumo-vermas" onClick={() => setVerMeses(v => !v)}>
            {verMeses ? 'Ocultar gasto por mes' : 'Ver gasto por mes'}
          </button>
          {verMeses && (
            <div className="al-cartola">
              {s.historial.map((h) => (
                <div key={h.desde} className="al-cartola-fila">
                  <span className="al-cartola-que">
                    {nombreMes(h.desde)}
                    <span className="al-cartola-fecha">
                      {consultas(h.consultas)}
                      {h.lecturasClp > 0 ? ` · ${clp(h.lecturasClp)} en lectura de archivos` : ''}
                      {h.devueltoClp > 0 ? ` · ${clp(h.devueltoClp)} devueltos` : ''}
                    </span>
                  </span>
                  <span className="al-cartola-menos">{clp(h.gastadoClp)}</span>
                </div>
              ))}
            </div>
          )}
        </>
      )}

      {s.movimientos.length > 0 && (
        <>
          <button className="al-consumo-vermas" onClick={() => setVerCartola(v => !v)}>
            {verCartola ? 'Ocultar movimientos' : 'Ver movimientos'}
          </button>
          {verCartola && (
            <div className="al-cartola">
              {s.movimientos.map(m => (
                <div key={m.id} className="al-cartola-fila">
                  <span className="al-cartola-que">
                    {m.tipo === 'carga'
                      ? 'Carga'
                      : m.tipo === 'bienvenida'
                        ? 'Saldo de bienvenida'
                        : m.tipo === 'ajuste'
                          ? 'Ajuste'
                          : m.tipo === 'cuota'
                            ? 'Cuota del mes (plan anterior)'
                            : m.tipo === 'prueba'
                              ? 'Consultas de prueba'
                              /* Distinguir la consulta de la lectura de un adjunto
                                 importa: si no, un descuento por transcribir una
                                 foto parece una consulta que nadie hizo. */
                              : m.origen === 'ocr'
                                ? 'Lectura de archivo'
                                : 'Consulta'}
                    <span className="al-cartola-fecha">
                      {fmtFecha(m.ts)}{m.tipo === 'consumo' && m.detalle ? ` · ${m.detalle}` : ''}
                    </span>
                  </span>
                  <span className={m.montoClp >= 0 ? 'al-cartola-mas' : 'al-cartola-menos'}>
                    {m.montoClp >= 0 ? '+' : '−'}{clp(Math.abs(m.montoClp))}
                  </span>
                </div>
              ))}
            </div>
          )}
        </>
      )}
    </div>
  );
};
